/**
 * ContentCatalog -- Curated content database for the Watch module.
 *
 * All content is free, legal, and family-safe. Sources include YouTube,
 * TED, BBC, DW, Al Jazeera, Khan Academy, and other educational platforms.
 */

export interface ContentItem {
  id: string;
  title: Record<string, string>;
  description: Record<string, string>;
  url: string;
  embed_url?: string;
  thumbnail?: string;
  source: string;
  type: 'video' | 'documentary' | 'podcast' | 'live_tv' | 'live_radio' | 'course' | 'channel';
  category: string;
  subcategory?: string;
  languages: string[];
  age_range: 'kids' | 'teens' | 'all' | 'adults';
  duration?: number;
  family_safe: boolean;
  featured?: boolean;
  tags: string[];
  /** Whether content can be played in-app via iframe. Defaults to true if embed_url is set. */
  embeddable?: boolean;
  /** Specific video IDs for channel-type entries (for in-app playback). */
  featured_videos?: string[];
}

/**
 * Determine if a content item can be embedded in-app.
 * Items with embed_url are embeddable by default unless explicitly marked otherwise.
 * Podcasts use AudioPlayer (always in-app). Non-embed items get a sandboxed iframe
 * or a "cannot embed" notice.
 */
export function isEmbeddable(item: ContentItem): boolean {
  if (item.embeddable === false) return false;
  if (item.embeddable === true) return true;
  // Default: embeddable if has embed_url or is a podcast
  return !!item.embed_url || item.type === 'podcast';
}

export type WatchTab = 'for_you' | 'documentaries' | 'learn' | 'kids' | 'podcasts' | 'live' | 'islamic';

// ---------- Source Badge Colors ----------

export const SOURCE_COLORS: Record<string, string> = {
  YouTube: '#FF0000',
  'TED': '#E62B1E',
  'TED-Ed': '#E62B1E',
  'Khan Academy': '#14BF96',
  BBC: '#BB1919',
  'BBC Earth': '#4A8C2A',
  DW: '#003D7A',
  'Al Jazeera': '#D2A026',
  'National Geographic': '#FFCE00',
  'France 24': '#0055A4',
  Arte: '#FA5400',
  NPR: '#6B6B6B',
  'France Inter': '#E4002B',
  Euronews: '#003399',
  Spotify: '#1DB954',
  'Apple Podcasts': '#9933CC',
  RFI: '#007BB8',
  Kurzgesagt: '#2196F3',
  Veritasium: '#333333',
  CrashCourse: '#4CAF50',
};

// ---------- Category Icons ----------

export const CATEGORY_ICONS: Record<string, string> = {
  science: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 3v6l-3 6h12l-3-6V3"/><path d="M6 15a6 6 0 0 0 12 0"/></svg>`,
  nature: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`,
  history: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`,
  math: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>`,
  languages: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 8l6 6"/><path d="M4 14l6-6 2-3"/><path d="M2 5h12"/><path d="M7 2h1"/><path d="M22 22l-5-10-5 10"/><path d="M14 18h6"/></svg>`,
  technology: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>`,
  geography: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`,
  arts: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="13.5" cy="6.5" r="2.5"/><circle cx="19" cy="11.5" r="2.5"/><circle cx="17" cy="18.5" r="2.5"/><circle cx="8" cy="18.5" r="2.5"/><circle cx="5" cy="11" r="2.5"/></svg>`,
  news: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 20H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v1m2 13a2 2 0 0 1-2-2V7m2 13a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2m-4-3H9"/></svg>`,
  quran: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>`,
  islamic: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`,
  stories: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>`,
};

// ---------- Language Flag Emojis ----------

export const LANG_FLAGS: Record<string, string> = {
  en: '\uD83C\uDDEC\uD83C\uDDE7',
  fr: '\uD83C\uDDEB\uD83C\uDDF7',
  ar: '\uD83C\uDDF8\uD83C\uDDE6',
  es: '\uD83C\uDDEA\uD83C\uDDF8',
  de: '\uD83C\uDDE9\uD83C\uDDEA',
  multi: '\uD83C\uDF0D',
};

// ---------- Curated Content Catalog ----------

export const CONTENT_CATALOG: ContentItem[] = [
  // ====================== DOCUMENTARIES ======================

  // Nature
  {
    id: 'doc-bbc-earth-1',
    title: { en: 'Our Planet - Coastal Seas', fr: 'Notre Planete - Mers cotieres', ar: '\u0643\u0648\u0643\u0628\u0646\u0627 - \u0627\u0644\u0628\u062D\u0627\u0631 \u0627\u0644\u0633\u0627\u062D\u0644\u064A\u0629', es: 'Nuestro Planeta - Mares costeros', de: 'Unser Planet - Kustenmeere' },
    description: { en: 'Explore the rich biodiversity of coastal waters.', fr: 'Explorez la riche biodiversite des eaux cotieres.', ar: '\u0627\u0633\u062A\u0643\u0634\u0641 \u0627\u0644\u062A\u0646\u0648\u0639 \u0627\u0644\u0628\u064A\u0648\u0644\u0648\u062C\u064A \u0627\u0644\u063A\u0646\u064A \u0644\u0644\u0645\u064A\u0627\u0647 \u0627\u0644\u0633\u0627\u062D\u0644\u064A\u0629.', es: 'Explora la rica biodiversidad de las aguas costeras.', de: 'Erkunden Sie die reiche Artenvielfalt der Kustengewasser.' },
    url: 'https://www.youtube.com/watch?v=RpTpJnXBJuI',
    embed_url: 'https://www.youtube-nocookie.com/embed/RpTpJnXBJuI',
    thumbnail: 'https://img.youtube.com/vi/RpTpJnXBJuI/hqdefault.jpg',
    source: 'BBC Earth',
    type: 'documentary',
    category: 'nature',
    languages: ['en'],
    age_range: 'all',
    duration: 50,
    family_safe: true,
    featured: true,
    tags: ['ocean', 'wildlife', 'conservation'],
  },
  {
    id: 'doc-natgeo-1',
    title: { en: 'The Story of Earth', fr: "L'Histoire de la Terre", ar: '\u0642\u0635\u0629 \u0627\u0644\u0623\u0631\u0636', es: 'La Historia de la Tierra', de: 'Die Geschichte der Erde' },
    description: { en: 'A journey through 4.5 billion years of planetary history.', fr: 'Un voyage a travers 4,5 milliards annees.', ar: '\u0631\u062D\u0644\u0629 \u0639\u0628\u0631 4.5 \u0645\u0644\u064A\u0627\u0631 \u0633\u0646\u0629 \u0645\u0646 \u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0643\u0648\u0643\u0628.', es: 'Un viaje a traves de 4.500 millones de anos.', de: 'Eine Reise durch 4,5 Milliarden Jahre Erdgeschichte.' },
    url: 'https://www.youtube.com/watch?v=Cg5UyIm9eQs',
    embed_url: 'https://www.youtube-nocookie.com/embed/Cg5UyIm9eQs',
    thumbnail: 'https://img.youtube.com/vi/Cg5UyIm9eQs/hqdefault.jpg',
    source: 'National Geographic',
    type: 'documentary',
    category: 'nature',
    languages: ['en'],
    age_range: 'all',
    duration: 90,
    family_safe: true,
    tags: ['earth', 'geology', 'history'],
  },
  {
    id: 'doc-bbc-blue-planet',
    title: { en: 'Blue Planet II - The Deep', fr: 'Planete Bleue II - Les Abysses', ar: '\u0627\u0644\u0643\u0648\u0643\u0628 \u0627\u0644\u0623\u0632\u0631\u0642 - \u0627\u0644\u0623\u0639\u0645\u0627\u0642', es: 'Planeta Azul II - Las Profundidades', de: 'Blauer Planet II - Die Tiefsee' },
    description: { en: 'Discover the mysterious creatures of the deep ocean.', fr: 'Decouvrez les creatures mysterieuses des profondeurs.', ar: '\u0627\u0643\u062A\u0634\u0641 \u0627\u0644\u0645\u062E\u0644\u0648\u0642\u0627\u062A \u0627\u0644\u063A\u0627\u0645\u0636\u0629 \u0641\u064A \u0623\u0639\u0645\u0627\u0642 \u0627\u0644\u0645\u062D\u064A\u0637.', es: 'Descubre las misteriosas criaturas del oceano profundo.', de: 'Entdecken Sie die geheimnisvollen Kreaturen der Tiefsee.' },
    url: 'https://www.youtube.com/watch?v=r9PnTSFfle0',
    embed_url: 'https://www.youtube-nocookie.com/embed/r9PnTSFfle0',
    thumbnail: 'https://img.youtube.com/vi/r9PnTSFfle0/hqdefault.jpg',
    source: 'BBC Earth',
    type: 'documentary',
    category: 'nature',
    languages: ['en'],
    age_range: 'all',
    duration: 60,
    family_safe: true,
    tags: ['ocean', 'deep sea', 'marine life'],
  },

  // Science Documentaries
  {
    id: 'doc-kurzgesagt-immune',
    title: { en: 'The Immune System Explained', fr: 'Le systeme immunitaire explique', ar: '\u0634\u0631\u062D \u062C\u0647\u0627\u0632 \u0627\u0644\u0645\u0646\u0627\u0639\u0629', es: 'El sistema inmunologico explicado', de: 'Das Immunsystem erklart' },
    description: { en: 'An animated journey through your immune system.', fr: "Un voyage anime a travers votre systeme immunitaire.", ar: '\u0631\u062D\u0644\u0629 \u0645\u062A\u062D\u0631\u0643\u0629 \u0639\u0628\u0631 \u062C\u0647\u0627\u0632 \u0627\u0644\u0645\u0646\u0627\u0639\u0629.', es: 'Un viaje animado por tu sistema inmunologico.', de: 'Eine animierte Reise durch Ihr Immunsystem.' },
    url: 'https://www.youtube.com/watch?v=lXfEK8G8CUI',
    embed_url: 'https://www.youtube-nocookie.com/embed/lXfEK8G8CUI',
    thumbnail: 'https://img.youtube.com/vi/lXfEK8G8CUI/hqdefault.jpg',
    source: 'Kurzgesagt',
    type: 'documentary',
    category: 'science',
    languages: ['en', 'de'],
    age_range: 'all',
    duration: 7,
    family_safe: true,
    featured: true,
    tags: ['biology', 'health', 'animation'],
  },
  {
    id: 'doc-veritasium-turbulence',
    title: { en: 'Turbulence: The Last Great Problem of Physics', fr: 'Turbulence: Le dernier grand probleme de la physique', ar: '\u0627\u0644\u0627\u0636\u0637\u0631\u0627\u0628: \u0622\u062E\u0631 \u0645\u0634\u0643\u0644\u0629 \u0643\u0628\u0631\u0649 \u0641\u064A \u0627\u0644\u0641\u064A\u0632\u064A\u0627\u0621', es: 'Turbulencia: El ultimo gran problema de la fisica', de: 'Turbulenz: Das letzte grosse Problem der Physik' },
    description: { en: 'Why is turbulence one of the unsolved problems in physics?', fr: 'Pourquoi la turbulence est-elle un probleme non resolu?', ar: '\u0644\u0645\u0627\u0630\u0627 \u0627\u0644\u0627\u0636\u0637\u0631\u0627\u0628 \u0645\u0646 \u0627\u0644\u0645\u0634\u0627\u0643\u0644 \u063A\u064A\u0631 \u0627\u0644\u0645\u062D\u0644\u0648\u0644\u0629\u061F', es: 'Por que la turbulencia es un problema sin resolver?', de: 'Warum ist Turbulenz eines der ungelosten Probleme der Physik?' },
    url: 'https://www.youtube.com/watch?v=5zI9sG3pjVU',
    embed_url: 'https://www.youtube-nocookie.com/embed/5zI9sG3pjVU',
    thumbnail: 'https://img.youtube.com/vi/5zI9sG3pjVU/hqdefault.jpg',
    source: 'Veritasium',
    type: 'documentary',
    category: 'science',
    languages: ['en'],
    age_range: 'teens',
    duration: 16,
    family_safe: true,
    tags: ['physics', 'fluid dynamics'],
  },
  {
    id: 'doc-smartereveryday-destin',
    title: { en: 'How Does a Helicopter Fly?', fr: 'Comment vole un helicoptere?', ar: '\u0643\u064A\u0641 \u064A\u0637\u064A\u0631 \u0627\u0644\u0645\u0631\u0648\u062D\u064A\u0629\u061F', es: 'Como vuela un helicoptero?', de: 'Wie fliegt ein Hubschrauber?' },
    description: { en: 'The fascinating engineering behind helicopter flight.', fr: "L'ingenierie fascinante derriere le vol en helicoptere.", ar: '\u0627\u0644\u0647\u0646\u062F\u0633\u0629 \u0627\u0644\u0631\u0627\u0626\u0639\u0629 \u0648\u0631\u0627\u0621 \u0637\u064A\u0631\u0627\u0646 \u0627\u0644\u0645\u0631\u0648\u062D\u064A\u0629.', es: 'La fascinante ingenieria detras del vuelo en helicoptero.', de: 'Die faszinierende Technik hinter dem Hubschrauberflug.' },
    url: 'https://www.youtube.com/watch?v=WdEWzqsfeHM',
    embed_url: 'https://www.youtube-nocookie.com/embed/WdEWzqsfeHM',
    thumbnail: 'https://img.youtube.com/vi/WdEWzqsfeHM/hqdefault.jpg',
    source: 'YouTube',
    type: 'documentary',
    category: 'science',
    subcategory: 'engineering',
    languages: ['en'],
    age_range: 'all',
    duration: 25,
    family_safe: true,
    tags: ['engineering', 'aviation', 'physics'],
  },

  // History Documentaries
  {
    id: 'doc-oversimplified-ww2',
    title: { en: 'World War II - OverSimplified', fr: 'La Seconde Guerre mondiale - Simplifie', ar: '\u0627\u0644\u062D\u0631\u0628 \u0627\u0644\u0639\u0627\u0644\u0645\u064A\u0629 \u0627\u0644\u062B\u0627\u0646\u064A\u0629 - \u0645\u0628\u0633\u0637', es: 'Segunda Guerra Mundial - Simplificado', de: 'Zweiter Weltkrieg - Vereinfacht' },
    description: { en: 'The complete story of WWII in an entertaining animated format.', fr: "L'histoire complete de la WWII en animation.", ar: '\u0627\u0644\u0642\u0635\u0629 \u0627\u0644\u0643\u0627\u0645\u0644\u0629 \u0644\u0644\u062D\u0631\u0628 \u0627\u0644\u0639\u0627\u0644\u0645\u064A\u0629 \u0627\u0644\u062B\u0627\u0646\u064A\u0629 \u0628\u0634\u0643\u0644 \u0645\u062A\u062D\u0631\u0643 \u0645\u0645\u062A\u0639.', es: 'La historia completa de la WWII en formato animado.', de: 'Die komplette Geschichte des WWII in unterhaltsamem Animationsformat.' },
    url: 'https://www.youtube.com/watch?v=_uk_6vfqwTA',
    embed_url: 'https://www.youtube-nocookie.com/embed/_uk_6vfqwTA',
    thumbnail: 'https://img.youtube.com/vi/_uk_6vfqwTA/hqdefault.jpg',
    source: 'YouTube',
    type: 'documentary',
    category: 'history',
    languages: ['en'],
    age_range: 'teens',
    duration: 28,
    family_safe: true,
    tags: ['world war', 'animation', 'education'],
  },
  {
    id: 'doc-extra-history-rome',
    title: { en: 'The Fall of Rome', fr: 'La Chute de Rome', ar: '\u0633\u0642\u0648\u0637 \u0631\u0648\u0645\u0627', es: 'La Caida de Roma', de: 'Der Fall Roms' },
    description: { en: 'How the greatest empire in history came to an end.', fr: 'Comment le plus grand empire est tombe.', ar: '\u0643\u064A\u0641 \u0627\u0646\u062A\u0647\u062A \u0623\u0639\u0638\u0645 \u0625\u0645\u0628\u0631\u0627\u0637\u0648\u0631\u064A\u0629 \u0641\u064A \u0627\u0644\u062A\u0627\u0631\u064A\u062E.', es: 'Como el mayor imperio de la historia llego a su fin.', de: 'Wie das grosste Reich der Geschichte unterging.' },
    url: 'https://www.youtube.com/watch?v=3PszVWZNWVA',
    embed_url: 'https://www.youtube-nocookie.com/embed/3PszVWZNWVA',
    thumbnail: 'https://img.youtube.com/vi/3PszVWZNWVA/hqdefault.jpg',
    source: 'YouTube',
    type: 'documentary',
    category: 'history',
    languages: ['en'],
    age_range: 'teens',
    duration: 12,
    family_safe: true,
    tags: ['rome', 'ancient history', 'civilization'],
  },
  {
    id: 'doc-dw-medieval',
    title: { en: 'Life in the Middle Ages', fr: 'La Vie au Moyen Age', ar: '\u0627\u0644\u062D\u064A\u0627\u0629 \u0641\u064A \u0627\u0644\u0639\u0635\u0648\u0631 \u0627\u0644\u0648\u0633\u0637\u0649', es: 'La Vida en la Edad Media', de: 'Das Leben im Mittelalter' },
    description: { en: 'What was daily life really like in medieval Europe?', fr: 'Comment etait la vie quotidienne en Europe medievale?', ar: '\u0643\u064A\u0641 \u0643\u0627\u0646\u062A \u0627\u0644\u062D\u064A\u0627\u0629 \u0627\u0644\u064A\u0648\u0645\u064A\u0629 \u0641\u064A \u0623\u0648\u0631\u0648\u0628\u0627 \u0627\u0644\u0642\u0631\u0648\u0633\u0637\u064A\u0629\u061F', es: 'Como era la vida cotidiana en la Europa medieval?', de: 'Wie war das tagliche Leben im mittelalterlichen Europa?' },
    url: 'https://www.youtube.com/watch?v=VXQh0YR5fWg',
    embed_url: 'https://www.youtube-nocookie.com/embed/VXQh0YR5fWg',
    thumbnail: 'https://img.youtube.com/vi/VXQh0YR5fWg/hqdefault.jpg',
    source: 'DW',
    type: 'documentary',
    category: 'history',
    languages: ['en', 'de'],
    age_range: 'all',
    duration: 42,
    family_safe: true,
    tags: ['medieval', 'europe', 'daily life'],
  },
  {
    id: 'doc-aljazeera-islamic-civilization',
    title: { en: 'The Golden Age of Islam', fr: "L'Age d'or de l'Islam", ar: '\u0627\u0644\u0639\u0635\u0631 \u0627\u0644\u0630\u0647\u0628\u064A \u0644\u0644\u0625\u0633\u0644\u0627\u0645', es: 'La Edad de Oro del Islam', de: 'Das Goldene Zeitalter des Islam' },
    description: { en: 'How Islamic civilization led the world in science, art, and philosophy.', fr: "Comment la civilisation islamique a mene le monde en science.", ar: '\u0643\u064A\u0641 \u0642\u0627\u062F\u062A \u0627\u0644\u062D\u0636\u0627\u0631\u0629 \u0627\u0644\u0625\u0633\u0644\u0627\u0645\u064A\u0629 \u0627\u0644\u0639\u0627\u0644\u0645 \u0641\u064A \u0627\u0644\u0639\u0644\u0645 \u0648\u0627\u0644\u0641\u0646 \u0648\u0627\u0644\u0641\u0644\u0633\u0641\u0629.', es: 'Como la civilizacion islamica lidero el mundo.', de: 'Wie die islamische Zivilisation die Welt anfuhrte.' },
    url: 'https://www.youtube.com/watch?v=Q5GZKOvWPMY',
    embed_url: 'https://www.youtube-nocookie.com/embed/Q5GZKOvWPMY',
    thumbnail: 'https://img.youtube.com/vi/Q5GZKOvWPMY/hqdefault.jpg',
    source: 'Al Jazeera',
    type: 'documentary',
    category: 'history',
    subcategory: 'islamic history',
    languages: ['en', 'ar'],
    age_range: 'all',
    duration: 48,
    family_safe: true,
    featured: true,
    tags: ['islamic history', 'science', 'civilization'],
  },
  {
    id: 'doc-dw-space',
    title: { en: 'The Mystery of Dark Matter', fr: 'Le Mystere de la matiere noire', ar: '\u0644\u063A\u0632 \u0627\u0644\u0645\u0627\u062F\u0629 \u0627\u0644\u0645\u0638\u0644\u0645\u0629', es: 'El Misterio de la Materia Oscura', de: 'Das Ratsel der Dunklen Materie' },
    description: { en: 'Scientists search for the invisible matter that shapes our universe.', fr: 'Les scientifiques cherchent la matiere invisible.', ar: '\u0627\u0644\u0639\u0644\u0645\u0627\u0621 \u064A\u0628\u062D\u062B\u0648\u0646 \u0639\u0646 \u0627\u0644\u0645\u0627\u062F\u0629 \u063A\u064A\u0631 \u0627\u0644\u0645\u0631\u0626\u064A\u0629.', es: 'Los cientificos buscan la materia invisible.', de: 'Wissenschaftler suchen nach der unsichtbaren Materie.' },
    url: 'https://www.youtube.com/watch?v=HneiEA1B8ks',
    embed_url: 'https://www.youtube-nocookie.com/embed/HneiEA1B8ks',
    thumbnail: 'https://img.youtube.com/vi/HneiEA1B8ks/hqdefault.jpg',
    source: 'DW',
    type: 'documentary',
    category: 'science',
    subcategory: 'space',
    languages: ['en', 'de', 'ar', 'es'],
    age_range: 'teens',
    duration: 42,
    family_safe: true,
    tags: ['space', 'physics', 'dark matter'],
  },
  {
    id: 'doc-arte-ocean',
    title: { en: 'The Secret World of the Ocean', fr: 'Le Monde secret de l\'ocean', ar: '\u0627\u0644\u0639\u0627\u0644\u0645 \u0627\u0644\u0633\u0631\u064A \u0644\u0644\u0645\u062D\u064A\u0637', es: 'El Mundo secreto del oceano', de: 'Die geheime Welt des Ozeans' },
    description: { en: 'A deep dive into the hidden world beneath the waves.', fr: 'Une plongee dans le monde cache sous les vagues.', ar: '\u063A\u0648\u0635\u0629 \u0639\u0645\u064A\u0642\u0629 \u0641\u064A \u0627\u0644\u0639\u0627\u0644\u0645 \u0627\u0644\u0645\u062E\u0641\u064A \u062A\u062D\u062A \u0627\u0644\u0623\u0645\u0648\u0627\u062C.', es: 'Una inmersion en el mundo oculto bajo las olas.', de: 'Ein tiefer Einblick in die verborgene Welt unter den Wellen.' },
    url: 'https://www.youtube.com/watch?v=x6rMN_TLxFs',
    embed_url: 'https://www.youtube-nocookie.com/embed/x6rMN_TLxFs',
    thumbnail: 'https://img.youtube.com/vi/x6rMN_TLxFs/hqdefault.jpg',
    source: 'Arte',
    type: 'documentary',
    category: 'nature',
    languages: ['fr', 'de'],
    age_range: 'all',
    duration: 52,
    family_safe: true,
    tags: ['ocean', 'nature', 'wildlife'],
  },
  {
    id: 'doc-kurzgesagt-black-holes',
    title: { en: 'Black Holes Explained', fr: 'Les Trous noirs expliques', ar: '\u0634\u0631\u062D \u0627\u0644\u062B\u0642\u0648\u0628 \u0627\u0644\u0633\u0648\u062F\u0627\u0621', es: 'Agujeros Negros explicados', de: 'Schwarze Locher erklart' },
    description: { en: 'What happens when you fall into a black hole?', fr: 'Que se passe-t-il quand on tombe dans un trou noir?', ar: '\u0645\u0627\u0630\u0627 \u064A\u062D\u062F\u062B \u0639\u0646\u062F\u0645\u0627 \u062A\u0633\u0642\u0637 \u0641\u064A \u062B\u0642\u0628 \u0623\u0633\u0648\u062F\u061F', es: 'Que pasa cuando caes en un agujero negro?', de: 'Was passiert, wenn man in ein Schwarzes Loch fallt?' },
    url: 'https://www.youtube.com/watch?v=e-P5IFTqB98',
    embed_url: 'https://www.youtube-nocookie.com/embed/e-P5IFTqB98',
    thumbnail: 'https://img.youtube.com/vi/e-P5IFTqB98/hqdefault.jpg',
    source: 'Kurzgesagt',
    type: 'documentary',
    category: 'science',
    subcategory: 'space',
    languages: ['en', 'de'],
    age_range: 'all',
    duration: 6,
    family_safe: true,
    tags: ['space', 'physics', 'black holes', 'animation'],
  },
  {
    id: 'doc-kurzgesagt-ants',
    title: { en: 'The World War of Ants', fr: 'La Guerre mondiale des fourmis', ar: '\u0627\u0644\u062D\u0631\u0628 \u0627\u0644\u0639\u0627\u0644\u0645\u064A\u0629 \u0644\u0644\u0646\u0645\u0644', es: 'La Guerra Mundial de las Hormigas', de: 'Der Weltkrieg der Ameisen' },
    description: { en: 'The epic wars of the largest ant colonies on Earth.', fr: 'Les guerres epiques des plus grandes colonies de fourmis.', ar: '\u0627\u0644\u062D\u0631\u0648\u0628 \u0627\u0644\u0645\u0644\u062D\u0645\u064A\u0629 \u0644\u0623\u0643\u0628\u0631 \u0645\u0633\u062A\u0639\u0645\u0631\u0627\u062A \u0627\u0644\u0646\u0645\u0644.', es: 'Las guerras epicas de las mayores colonias de hormigas.', de: 'Die epischen Kriege der grossten Ameisenkolonien.' },
    url: 'https://www.youtube.com/watch?v=7_e0CA_nhaE',
    embed_url: 'https://www.youtube-nocookie.com/embed/7_e0CA_nhaE',
    thumbnail: 'https://img.youtube.com/vi/7_e0CA_nhaE/hqdefault.jpg',
    source: 'Kurzgesagt',
    type: 'documentary',
    category: 'nature',
    subcategory: 'insects',
    languages: ['en', 'de'],
    age_range: 'all',
    duration: 8,
    family_safe: true,
    tags: ['ants', 'nature', 'biology', 'animation'],
  },

  // ====================== EDUCATIONAL ======================

  {
    id: 'edu-khan-algebra',
    title: { en: 'Algebra Basics', fr: "Les Bases de l'algebre", ar: '\u0623\u0633\u0627\u0633\u064A\u0627\u062A \u0627\u0644\u062C\u0628\u0631', es: 'Bases del Algebra', de: 'Algebra Grundlagen' },
    description: { en: 'Learn algebra from the ground up with Khan Academy.', fr: "Apprenez l'algebre avec Khan Academy.", ar: '\u062A\u0639\u0644\u0645 \u0627\u0644\u062C\u0628\u0631 \u0645\u0646 \u0627\u0644\u0623\u0633\u0627\u0633 \u0645\u0639 \u0623\u0643\u0627\u062F\u064A\u0645\u064A\u0629 \u062E\u0627\u0646.', es: 'Aprende algebra desde cero con Khan Academy.', de: 'Algebra von Grund auf mit Khan Academy lernen.' },
    url: 'https://www.youtube.com/watch?v=NybHckSEQBI',
    embed_url: 'https://www.youtube-nocookie.com/embed/NybHckSEQBI',
    thumbnail: 'https://img.youtube.com/vi/NybHckSEQBI/hqdefault.jpg',
    source: 'Khan Academy',
    type: 'course',
    category: 'math',
    languages: ['en', 'fr', 'es'],
    age_range: 'teens',
    duration: 18,
    family_safe: true,
    tags: ['algebra', 'math', 'education'],
  },
  {
    id: 'edu-ted-ed-brain',
    title: { en: 'How Does Your Brain Work?', fr: 'Comment fonctionne votre cerveau?', ar: '\u0643\u064A\u0641 \u064A\u0639\u0645\u0644 \u062F\u0645\u0627\u063A\u0643\u061F', es: 'Como funciona tu cerebro?', de: 'Wie funktioniert Ihr Gehirn?' },
    description: { en: 'A TED-Ed lesson on the incredible human brain.', fr: 'Une lecon TED-Ed sur le cerveau humain.', ar: '\u062F\u0631\u0633 TED-Ed \u062D\u0648\u0644 \u0627\u0644\u062F\u0645\u0627\u063A \u0627\u0644\u0628\u0634\u0631\u064A.', es: 'Una leccion TED-Ed sobre el cerebro humano.', de: 'Eine TED-Ed Lektion uber das menschliche Gehirn.' },
    url: 'https://www.youtube.com/watch?v=JVf-5cL6TWs',
    embed_url: 'https://www.youtube-nocookie.com/embed/JVf-5cL6TWs',
    thumbnail: 'https://img.youtube.com/vi/JVf-5cL6TWs/hqdefault.jpg',
    source: 'TED-Ed',
    type: 'video',
    category: 'science',
    subcategory: 'neuroscience',
    languages: ['en'],
    age_range: 'all',
    duration: 5,
    family_safe: true,
    tags: ['brain', 'neuroscience', 'animation'],
  },
  {
    id: 'edu-3b1b-linear-algebra',
    title: { en: 'Essence of Linear Algebra', fr: "L'Essence de l'algebre lineaire", ar: '\u062C\u0648\u0647\u0631 \u0627\u0644\u062C\u0628\u0631 \u0627\u0644\u062E\u0637\u064A', es: 'Esencia del Algebra Lineal', de: 'Essenz der Linearen Algebra' },
    description: { en: 'Geometric intuition for linear algebra concepts.', fr: 'Intuition geometrique pour les concepts algebriques.', ar: '\u062D\u062F\u0633 \u0647\u0646\u062F\u0633\u064A \u0644\u0645\u0641\u0627\u0647\u064A\u0645 \u0627\u0644\u062C\u0628\u0631 \u0627\u0644\u062E\u0637\u064A.', es: 'Intuicion geometrica para el algebra lineal.', de: 'Geometrische Intuition fur lineare Algebra.' },
    url: 'https://www.youtube.com/watch?v=fNk_zzaMoSs',
    embed_url: 'https://www.youtube-nocookie.com/embed/fNk_zzaMoSs',
    thumbnail: 'https://img.youtube.com/vi/fNk_zzaMoSs/hqdefault.jpg',
    source: 'YouTube',
    type: 'course',
    category: 'math',
    languages: ['en'],
    age_range: 'teens',
    duration: 16,
    family_safe: true,
    featured: true,
    tags: ['linear algebra', 'math', 'visualization'],
  },
  {
    id: 'edu-crashcourse-biology',
    title: { en: 'Biology: The Study of Life', fr: 'Biologie: Science de la vie', ar: '\u0627\u0644\u0623\u062D\u064A\u0627\u0621: \u0639\u0644\u0645 \u0627\u0644\u062D\u064A\u0627\u0629', es: 'Biologia: El Estudio de la Vida', de: 'Biologie: Die Wissenschaft des Lebens' },
    description: { en: 'CrashCourse introduction to the science of life.', fr: 'Introduction CrashCourse aux sciences de la vie.', ar: '\u0645\u0642\u062F\u0645\u0629 CrashCourse \u0644\u0639\u0644\u0645 \u0627\u0644\u0623\u062D\u064A\u0627\u0621.', es: 'Introduccion CrashCourse a la ciencia de la vida.', de: 'CrashCourse Einfuhrung in die Biologie.' },
    url: 'https://www.youtube.com/watch?v=QnQe0xW_JY4',
    embed_url: 'https://www.youtube-nocookie.com/embed/QnQe0xW_JY4',
    thumbnail: 'https://img.youtube.com/vi/QnQe0xW_JY4/hqdefault.jpg',
    source: 'CrashCourse',
    type: 'course',
    category: 'science',
    subcategory: 'biology',
    languages: ['en'],
    age_range: 'teens',
    duration: 12,
    family_safe: true,
    tags: ['biology', 'science', 'education'],
  },
  {
    id: 'edu-numberphile-pi',
    title: { en: 'Pi and the Size of the Universe', fr: 'Pi et la Taille de l\'Univers', ar: '\u0628\u0627\u064A \u0648\u062D\u062C\u0645 \u0627\u0644\u0643\u0648\u0646', es: 'Pi y el Tamano del Universo', de: 'Pi und die Grosse des Universums' },
    description: { en: 'How many digits of Pi do you actually need?', fr: 'De combien de chiffres de Pi avez-vous besoin?', ar: '\u0643\u0645 \u0631\u0642\u0645 \u0645\u0646 \u0628\u0627\u064A \u062A\u062D\u062A\u0627\u062C \u0641\u0639\u0644\u0627\u064B\u061F', es: 'Cuantos digitos de Pi necesitas realmente?', de: 'Wie viele Stellen von Pi braucht man wirklich?' },
    url: 'https://www.youtube.com/watch?v=FpyrF_Ci2TQ',
    embed_url: 'https://www.youtube-nocookie.com/embed/FpyrF_Ci2TQ',
    thumbnail: 'https://img.youtube.com/vi/FpyrF_Ci2TQ/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'math',
    languages: ['en'],
    age_range: 'teens',
    duration: 9,
    family_safe: true,
    tags: ['pi', 'math', 'numbers'],
  },
  {
    id: 'edu-vsauce-counting',
    title: { en: 'How to Count Past Infinity', fr: 'Comment compter au-dela de l\'infini', ar: '\u0643\u064A\u0641 \u062A\u0639\u062F \u0645\u0627 \u0628\u0639\u062F \u0627\u0644\u0644\u0627\u0646\u0647\u0627\u064A\u0629', es: 'Como contar mas alla del infinito', de: 'Wie man uber die Unendlichkeit hinaus zahlt' },
    description: { en: 'Vsauce explores the mind-bending concept of infinity.', fr: 'Vsauce explore le concept déroutant de l\'infini.', ar: 'Vsauce \u064A\u0633\u062A\u0643\u0634\u0641 \u0645\u0641\u0647\u0648\u0645 \u0627\u0644\u0644\u0627\u0646\u0647\u0627\u064A\u0629.', es: 'Vsauce explora el concepto del infinito.', de: 'Vsauce erforscht das Konzept der Unendlichkeit.' },
    url: 'https://www.youtube.com/watch?v=SrU9YDoXE88',
    embed_url: 'https://www.youtube-nocookie.com/embed/SrU9YDoXE88',
    thumbnail: 'https://img.youtube.com/vi/SrU9YDoXE88/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'math',
    languages: ['en'],
    age_range: 'teens',
    duration: 23,
    family_safe: true,
    tags: ['infinity', 'math', 'philosophy'],
  },
  {
    id: 'edu-ted-procrastination',
    title: { en: 'Inside the Mind of a Procrastinator', fr: "Dans l'esprit d'un procrastinateur", ar: '\u062F\u0627\u062E\u0644 \u0639\u0642\u0644 \u0627\u0644\u0645\u0645\u0627\u0637\u0644', es: 'Dentro de la Mente de un Procrastinador', de: 'Im Kopf eines Prokrastinators' },
    description: { en: 'Tim Urban explains why procrastinators procrastinate.', fr: 'Tim Urban explique pourquoi on procrastine.', ar: '\u062A\u064A\u0645 \u0623\u0648\u0631\u0628\u0627\u0646 \u064A\u0634\u0631\u062D \u0644\u0645\u0627\u0630\u0627 \u0646\u0645\u0627\u0637\u0644.', es: 'Tim Urban explica por que procrastinamos.', de: 'Tim Urban erklart, warum wir prokrastinieren.' },
    url: 'https://www.youtube.com/watch?v=arj7oStGLkU',
    embed_url: 'https://www.youtube-nocookie.com/embed/arj7oStGLkU',
    thumbnail: 'https://img.youtube.com/vi/arj7oStGLkU/hqdefault.jpg',
    source: 'TED',
    type: 'video',
    category: 'science',
    subcategory: 'psychology',
    languages: ['en'],
    age_range: 'all',
    duration: 14,
    family_safe: true,
    featured: true,
    tags: ['psychology', 'productivity', 'ted talk'],
  },
  {
    id: 'edu-crashcourse-chemistry',
    title: { en: 'The Periodic Table', fr: 'Le Tableau periodique', ar: '\u0627\u0644\u062C\u062F\u0648\u0644 \u0627\u0644\u062F\u0648\u0631\u064A', es: 'La Tabla Periodica', de: 'Das Periodensystem' },
    description: { en: 'Everything you need to know about the periodic table.', fr: 'Tout sur le tableau periodique.', ar: '\u0643\u0644 \u0645\u0627 \u062A\u062D\u062A\u0627\u062C \u0645\u0639\u0631\u0641\u062A\u0647 \u0639\u0646 \u0627\u0644\u062C\u062F\u0648\u0644 \u0627\u0644\u062F\u0648\u0631\u064A.', es: 'Todo lo que necesitas saber sobre la tabla periodica.', de: 'Alles, was Sie uber das Periodensystem wissen mussen.' },
    url: 'https://www.youtube.com/watch?v=0RRVV4Diomg',
    embed_url: 'https://www.youtube-nocookie.com/embed/0RRVV4Diomg',
    thumbnail: 'https://img.youtube.com/vi/0RRVV4Diomg/hqdefault.jpg',
    source: 'CrashCourse',
    type: 'course',
    category: 'science',
    subcategory: 'chemistry',
    languages: ['en'],
    age_range: 'teens',
    duration: 11,
    family_safe: true,
    tags: ['chemistry', 'periodic table', 'elements'],
  },
  {
    id: 'edu-cps-volcans',
    title: { en: "It's Not Sorcery - Volcanoes", fr: "C'est pas sorcier - Les Volcans", ar: '\u0644\u064A\u0633 \u0633\u062D\u0631\u0627\u064B - \u0627\u0644\u0628\u0631\u0627\u0643\u064A\u0646', es: 'No es Magia - Los Volcanes', de: 'Keine Zauberei - Vulkane' },
    description: { en: "France's beloved science show explains volcanoes.", fr: "L'emission culte explique les volcans.", ar: '\u0627\u0644\u0628\u0631\u0646\u0627\u0645\u062C \u0627\u0644\u0639\u0644\u0645\u064A \u0627\u0644\u0641\u0631\u0646\u0633\u064A \u064A\u0634\u0631\u062D \u0627\u0644\u0628\u0631\u0627\u0643\u064A\u0646.', es: 'El programa de ciencia frances explica los volcanes.', de: 'Die franzosische Wissenschaftssendung erklart Vulkane.' },
    url: 'https://www.youtube.com/watch?v=0C-Gw-F7ykU',
    embed_url: 'https://www.youtube-nocookie.com/embed/0C-Gw-F7ykU',
    thumbnail: 'https://img.youtube.com/vi/0C-Gw-F7ykU/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'science',
    subcategory: 'geology',
    languages: ['fr'],
    age_range: 'kids',
    duration: 26,
    family_safe: true,
    tags: ['volcanoes', 'geology', 'kids science', 'french'],
  },
  {
    id: 'edu-dw-climate',
    title: { en: 'Climate Change Explained', fr: 'Le Changement climatique explique', ar: '\u0634\u0631\u062D \u062A\u063A\u064A\u0631 \u0627\u0644\u0645\u0646\u0627\u062E', es: 'El Cambio Climatico explicado', de: 'Klimawandel erklart' },
    description: { en: 'DW explains the science behind climate change.', fr: 'DW explique la science derriere le changement climatique.', ar: 'DW \u064A\u0634\u0631\u062D \u0627\u0644\u0639\u0644\u0645 \u0648\u0631\u0627\u0621 \u062A\u063A\u064A\u0631 \u0627\u0644\u0645\u0646\u0627\u062E.', es: 'DW explica la ciencia detras del cambio climatico.', de: 'DW erklart die Wissenschaft hinter dem Klimawandel.' },
    url: 'https://www.youtube.com/watch?v=G4H1N_yXBiA',
    embed_url: 'https://www.youtube-nocookie.com/embed/G4H1N_yXBiA',
    thumbnail: 'https://img.youtube.com/vi/G4H1N_yXBiA/hqdefault.jpg',
    source: 'DW',
    type: 'video',
    category: 'science',
    subcategory: 'environment',
    languages: ['en', 'de', 'ar', 'es'],
    age_range: 'all',
    duration: 12,
    family_safe: true,
    tags: ['climate', 'environment', 'science'],
  },
  {
    id: 'edu-ted-ed-photosynthesis',
    title: { en: 'How Photosynthesis Works', fr: 'Comment fonctionne la photosynthese', ar: '\u0643\u064A\u0641 \u064A\u0639\u0645\u0644 \u0627\u0644\u062A\u0645\u062B\u064A\u0644 \u0627\u0644\u0636\u0648\u0626\u064A', es: 'Como funciona la Fotosintesis', de: 'Wie Photosynthese funktioniert' },
    description: { en: 'A TED-Ed lesson on how plants convert sunlight into energy.', fr: 'Comment les plantes convertissent la lumiere en energie.', ar: '\u0643\u064A\u0641 \u062A\u062D\u0648\u0644 \u0627\u0644\u0646\u0628\u0627\u062A\u0627\u062A \u0636\u0648\u0621 \u0627\u0644\u0634\u0645\u0633 \u0625\u0644\u0649 \u0637\u0627\u0642\u0629.', es: 'Como las plantas convierten la luz solar en energia.', de: 'Wie Pflanzen Sonnenlicht in Energie umwandeln.' },
    url: 'https://www.youtube.com/watch?v=UPBMG5EYydo',
    embed_url: 'https://www.youtube-nocookie.com/embed/UPBMG5EYydo',
    thumbnail: 'https://img.youtube.com/vi/UPBMG5EYydo/hqdefault.jpg',
    source: 'TED-Ed',
    type: 'video',
    category: 'science',
    subcategory: 'biology',
    languages: ['en'],
    age_range: 'all',
    duration: 6,
    family_safe: true,
    tags: ['photosynthesis', 'biology', 'plants'],
  },
  {
    id: 'edu-crashcourse-world-history',
    title: { en: 'The Agricultural Revolution', fr: 'La Revolution Agricole', ar: '\u0627\u0644\u062B\u0648\u0631\u0629 \u0627\u0644\u0632\u0631\u0627\u0639\u064A\u0629', es: 'La Revolucion Agricola', de: 'Die Agrarrevolution' },
    description: { en: 'How farming changed human civilization forever.', fr: "Comment l'agriculture a change la civilisation.", ar: '\u0643\u064A\u0641 \u063A\u064A\u0631\u062A \u0627\u0644\u0632\u0631\u0627\u0639\u0629 \u0627\u0644\u062D\u0636\u0627\u0631\u0629 \u0627\u0644\u0628\u0634\u0631\u064A\u0629.', es: 'Como la agricultura cambio la civilizacion.', de: 'Wie die Landwirtschaft die Zivilisation veranderte.' },
    url: 'https://www.youtube.com/watch?v=Yocja_N5s1I',
    embed_url: 'https://www.youtube-nocookie.com/embed/Yocja_N5s1I',
    thumbnail: 'https://img.youtube.com/vi/Yocja_N5s1I/hqdefault.jpg',
    source: 'CrashCourse',
    type: 'course',
    category: 'history',
    languages: ['en'],
    age_range: 'teens',
    duration: 11,
    family_safe: true,
    tags: ['agriculture', 'civilization', 'world history'],
  },

  // ====================== KIDS ======================

  {
    id: 'kids-scishow-animals',
    title: { en: 'SciShow Kids - Amazing Animals', fr: 'SciShow Kids - Animaux incroyables', ar: 'SciShow Kids - \u062D\u064A\u0648\u0627\u0646\u0627\u062A \u0645\u062F\u0647\u0634\u0629', es: 'SciShow Kids - Animales asombrosos', de: 'SciShow Kids - Erstaunliche Tiere' },
    description: { en: 'Fun science facts about the animal kingdom for young learners.', fr: 'Faits scientifiques amusants pour les jeunes.', ar: '\u062D\u0642\u0627\u0626\u0642 \u0639\u0644\u0645\u064A\u0629 \u0645\u0645\u062A\u0639\u0629 \u0644\u0644\u0623\u0637\u0641\u0627\u0644.', es: 'Datos cientificos divertidos para los pequenos.', de: 'Lustige Wissenschaftsfakten fur junge Lerner.' },
    url: 'https://www.youtube.com/watch?v=Yqw55QXFJXU',
    embed_url: 'https://www.youtube-nocookie.com/embed/Yqw55QXFJXU',
    thumbnail: 'https://img.youtube.com/vi/Yqw55QXFJXU/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'nature',
    languages: ['en'],
    age_range: 'kids',
    duration: 5,
    family_safe: true,
    tags: ['animals', 'kids science', 'fun facts'],
  },
  {
    id: 'kids-natgeo-wild',
    title: { en: 'National Geographic Kids - Wild Animals', fr: 'Nat Geo Kids - Animaux sauvages', ar: 'Nat Geo Kids - \u0627\u0644\u062D\u064A\u0648\u0627\u0646\u0627\u062A \u0627\u0644\u0628\u0631\u064A\u0629', es: 'Nat Geo Kids - Animales Salvajes', de: 'Nat Geo Kids - Wilde Tiere' },
    description: { en: 'Learn about wild animals from around the world.', fr: 'Decouvrez les animaux sauvages du monde.', ar: '\u062A\u0639\u0631\u0641 \u0639\u0644\u0649 \u0627\u0644\u062D\u064A\u0648\u0627\u0646\u0627\u062A \u0627\u0644\u0628\u0631\u064A\u0629 \u0645\u0646 \u062D\u0648\u0644 \u0627\u0644\u0639\u0627\u0644\u0645.', es: 'Aprende sobre animales salvajes del mundo.', de: 'Erfahren Sie mehr uber wilde Tiere aus aller Welt.' },
    url: 'https://www.youtube.com/watch?v=hYsHQ1e5Hgk',
    embed_url: 'https://www.youtube-nocookie.com/embed/hYsHQ1e5Hgk',
    thumbnail: 'https://img.youtube.com/vi/hYsHQ1e5Hgk/hqdefault.jpg',
    source: 'National Geographic',
    type: 'video',
    category: 'nature',
    languages: ['en'],
    age_range: 'kids',
    duration: 4,
    family_safe: true,
    featured: true,
    tags: ['animals', 'kids', 'wildlife'],
  },
  {
    id: 'kids-storybots-earth',
    title: { en: 'StoryBots - Earth', fr: 'StoryBots - La Terre', ar: 'StoryBots - \u0627\u0644\u0623\u0631\u0636', es: 'StoryBots - La Tierra', de: 'StoryBots - Die Erde' },
    description: { en: 'Learn about planet Earth with the StoryBots.', fr: 'Decouvrez la planete Terre avec les StoryBots.', ar: '\u062A\u0639\u0644\u0645 \u0639\u0646 \u0643\u0648\u0643\u0628 \u0627\u0644\u0623\u0631\u0636 \u0645\u0639 StoryBots.', es: 'Aprende sobre el planeta Tierra con los StoryBots.', de: 'Lerne uber den Planeten Erde mit den StoryBots.' },
    url: 'https://www.youtube.com/watch?v=Uo2SNtFofWI',
    embed_url: 'https://www.youtube-nocookie.com/embed/Uo2SNtFofWI',
    thumbnail: 'https://img.youtube.com/vi/Uo2SNtFofWI/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'science',
    languages: ['en'],
    age_range: 'kids',
    duration: 3,
    family_safe: true,
    tags: ['earth', 'kids', 'planets', 'animation'],
  },
  {
    id: 'kids-sesame-abc',
    title: { en: 'Sesame Street - ABCs and 123s', fr: '1 rue Sesame - ABC et 123', ar: '\u0634\u0627\u0631\u0639 \u0633\u0645\u0633\u0645 - \u0627\u0644\u062D\u0631\u0648\u0641 \u0648\u0627\u0644\u0623\u0631\u0642\u0627\u0645', es: 'Plaza Sesamo - ABCs y 123s', de: 'Sesamstrasse - ABCs und 123s' },
    description: { en: 'Letters and numbers with your favorite Sesame Street characters.', fr: 'Lettres et chiffres avec vos personnages preferes.', ar: '\u0627\u0644\u062D\u0631\u0648\u0641 \u0648\u0627\u0644\u0623\u0631\u0642\u0627\u0645 \u0645\u0639 \u0634\u062E\u0635\u064A\u0627\u062A\u0643 \u0627\u0644\u0645\u0641\u0636\u0644\u0629.', es: 'Letras y numeros con tus personajes favoritos.', de: 'Buchstaben und Zahlen mit deinen Lieblingscharakteren.' },
    url: 'https://www.youtube.com/watch?v=UBNXDXNOKlo',
    embed_url: 'https://www.youtube-nocookie.com/embed/UBNXDXNOKlo',
    thumbnail: 'https://img.youtube.com/vi/UBNXDXNOKlo/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'languages',
    languages: ['en'],
    age_range: 'kids',
    duration: 30,
    family_safe: true,
    tags: ['abc', 'numbers', 'kids', 'learning'],
  },
  {
    id: 'kids-il-etait-espace',
    title: { en: 'Once Upon a Time... Space', fr: 'Il etait une fois... l\'Espace', ar: '\u0643\u0627\u0646 \u064A\u0627 \u0645\u0627 \u0643\u0627\u0646... \u0627\u0644\u0641\u0636\u0627\u0621', es: 'Erase una vez... El Espacio', de: 'Es war einmal... der Weltraum' },
    description: { en: 'The classic French animated series about space exploration.', fr: 'La serie animee culte sur l\'exploration spatiale.', ar: '\u0627\u0644\u0645\u0633\u0644\u0633\u0644 \u0627\u0644\u0641\u0631\u0646\u0633\u064A \u0627\u0644\u0643\u0644\u0627\u0633\u064A\u0643\u064A \u0639\u0646 \u0627\u0633\u062A\u0643\u0634\u0627\u0641 \u0627\u0644\u0641\u0636\u0627\u0621.', es: 'La clasica serie animada sobre la exploracion espacial.', de: 'Die klassische franzosische Zeichentrickserie uber Weltraumforschung.' },
    url: 'https://www.youtube.com/watch?v=Gu7JGqMb3yQ',
    embed_url: 'https://www.youtube-nocookie.com/embed/Gu7JGqMb3yQ',
    thumbnail: 'https://img.youtube.com/vi/Gu7JGqMb3yQ/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'science',
    subcategory: 'space',
    languages: ['fr'],
    age_range: 'kids',
    duration: 25,
    family_safe: true,
    tags: ['space', 'animation', 'classic', 'french'],
  },
  {
    id: 'kids-zaky-arabic',
    title: { en: 'Learn Arabic with Zaky', fr: 'Apprendre l\'arabe avec Zaky', ar: '\u062A\u0639\u0644\u0645 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0645\u0639 \u0632\u0643\u064A', es: 'Aprende arabe con Zaky', de: 'Arabisch lernen mit Zaky' },
    description: { en: 'Fun Arabic learning for children with Zaky the bear.', fr: 'Apprentissage de l\'arabe pour enfants avec Zaky.', ar: '\u062A\u0639\u0644\u0645 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0628\u0637\u0631\u064A\u0642\u0629 \u0645\u0645\u062A\u0639\u0629 \u0645\u0639 \u0632\u0643\u064A.', es: 'Aprendizaje del arabe para ninos con Zaky.', de: 'Arabisch lernen fur Kinder mit Zaky dem Baren.' },
    url: 'https://www.youtube.com/watch?v=YxJhwMKBlQo',
    embed_url: 'https://www.youtube-nocookie.com/embed/YxJhwMKBlQo',
    thumbnail: 'https://img.youtube.com/vi/YxJhwMKBlQo/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'languages',
    languages: ['ar', 'en'],
    age_range: 'kids',
    duration: 15,
    family_safe: true,
    tags: ['arabic', 'kids', 'language learning', 'islamic'],
  },
  {
    id: 'kids-pocoyo-es',
    title: { en: 'Pocoyo - Adventures', fr: 'Pocoyo - Aventures', ar: 'Pocoyo - \u0645\u063A\u0627\u0645\u0631\u0627\u062A', es: 'Pocoyo - Aventuras', de: 'Pocoyo - Abenteuer' },
    description: { en: 'Fun animated adventures with Pocoyo and friends.', fr: 'Aventures animees avec Pocoyo et ses amis.', ar: '\u0645\u063A\u0627\u0645\u0631\u0627\u062A \u0645\u062A\u062D\u0631\u0643\u0629 \u0645\u0639 \u0628\u0648\u0643\u0648\u064A\u0648 \u0648\u0623\u0635\u062F\u0642\u0627\u0626\u0647.', es: 'Divertidas aventuras animadas con Pocoyo y amigos.', de: 'Lustige Zeichentrickabenteuer mit Pocoyo und Freunden.' },
    url: 'https://www.youtube.com/watch?v=gSNSDqXGqFM',
    embed_url: 'https://www.youtube-nocookie.com/embed/gSNSDqXGqFM',
    thumbnail: 'https://img.youtube.com/vi/gSNSDqXGqFM/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'stories',
    languages: ['es'],
    age_range: 'kids',
    duration: 30,
    family_safe: true,
    tags: ['animation', 'kids', 'spanish', 'fun'],
  },
  {
    id: 'kids-maus-de',
    title: { en: 'Die Sendung mit der Maus', fr: 'L\'emission avec la souris', ar: '\u0628\u0631\u0646\u0627\u0645\u062C \u0627\u0644\u0641\u0623\u0631', es: 'El programa del Raton', de: 'Die Sendung mit der Maus' },
    description: { en: 'Germany\'s most beloved children\'s educational show.', fr: 'L\'emission pour enfants la plus aimee d\'Allemagne.', ar: '\u0623\u0634\u0647\u0631 \u0628\u0631\u0646\u0627\u0645\u062C \u062A\u0639\u0644\u064A\u0645\u064A \u0644\u0644\u0623\u0637\u0641\u0627\u0644 \u0641\u064A \u0623\u0644\u0645\u0627\u0646\u064A\u0627.', es: 'El programa infantil mas querido de Alemania.', de: 'Deutschlands beliebteste Kindersendung.' },
    url: 'https://www.youtube.com/watch?v=n3HREG0oPJo',
    embed_url: 'https://www.youtube-nocookie.com/embed/n3HREG0oPJo',
    thumbnail: 'https://img.youtube.com/vi/n3HREG0oPJo/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'science',
    languages: ['de'],
    age_range: 'kids',
    duration: 30,
    family_safe: true,
    tags: ['german', 'kids', 'education', 'classic'],
  },
  {
    id: 'kids-brains-on-space',
    title: { en: 'Brains On! - Mars Exploration', fr: 'Brains On! - Exploration de Mars', ar: 'Brains On! - \u0627\u0633\u062A\u0643\u0634\u0627\u0641 \u0627\u0644\u0645\u0631\u064A\u062E', es: 'Brains On! - Exploracion de Marte', de: 'Brains On! - Mars-Erkundung' },
    description: { en: 'A fun podcast about Mars for curious kids.', fr: 'Un podcast amusant sur Mars pour enfants curieux.', ar: '\u0628\u0648\u062F\u0643\u0627\u0633\u062A \u0645\u0645\u062A\u0639 \u0639\u0646 \u0627\u0644\u0645\u0631\u064A\u062E \u0644\u0644\u0623\u0637\u0641\u0627\u0644.', es: 'Un podcast divertido sobre Marte para ninos curiosos.', de: 'Ein lustiger Podcast uber den Mars fur neugierige Kinder.' },
    url: 'https://www.brainson.org/collection/space',
    source: 'YouTube',
    type: 'podcast',
    category: 'science',
    subcategory: 'space',
    languages: ['en'],
    age_range: 'kids',
    duration: 25,
    family_safe: true,
    tags: ['mars', 'space', 'kids podcast'],
  },
  {
    id: 'kids-cps-corps',
    title: { en: "It's Not Sorcery - The Human Body", fr: "C'est pas sorcier - Le Corps humain", ar: '\u0644\u064A\u0633 \u0633\u062D\u0631\u0627\u064B - \u062C\u0633\u0645 \u0627\u0644\u0625\u0646\u0633\u0627\u0646', es: 'No es Magia - El Cuerpo Humano', de: 'Keine Zauberei - Der menschliche Korper' },
    description: { en: 'Discover how the human body works in this classic French show.', fr: "Decouvrez comment le corps humain fonctionne.", ar: '\u0627\u0643\u062A\u0634\u0641 \u0643\u064A\u0641 \u064A\u0639\u0645\u0644 \u062C\u0633\u0645 \u0627\u0644\u0625\u0646\u0633\u0627\u0646.', es: 'Descubre como funciona el cuerpo humano.', de: 'Entdecken Sie, wie der menschliche Korper funktioniert.' },
    url: 'https://www.youtube.com/watch?v=aq7SKn4g-uo',
    embed_url: 'https://www.youtube-nocookie.com/embed/aq7SKn4g-uo',
    thumbnail: 'https://img.youtube.com/vi/aq7SKn4g-uo/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'science',
    subcategory: 'biology',
    languages: ['fr'],
    age_range: 'kids',
    duration: 26,
    family_safe: true,
    tags: ['human body', 'biology', 'french', 'kids'],
  },
  {
    id: 'kids-cocomelon-numbers',
    title: { en: 'Learning Numbers and Counting', fr: 'Apprendre les Nombres', ar: '\u062A\u0639\u0644\u0645 \u0627\u0644\u0623\u0631\u0642\u0627\u0645 \u0648\u0627\u0644\u0639\u062F', es: 'Aprendiendo Numeros y a Contar', de: 'Zahlen und Zahlen lernen' },
    description: { en: 'Fun counting songs for little ones.', fr: 'Chansons amusantes pour les tout-petits.', ar: '\u0623\u063A\u0627\u0646\u064A \u0639\u062F \u0645\u0645\u062A\u0639\u0629 \u0644\u0644\u0635\u063A\u0627\u0631.', es: 'Canciones divertidas para contar.', de: 'Lustige Zahllieder fur die Kleinen.' },
    url: 'https://www.youtube.com/watch?v=DR-cfDsHCGA',
    embed_url: 'https://www.youtube-nocookie.com/embed/DR-cfDsHCGA',
    thumbnail: 'https://img.youtube.com/vi/DR-cfDsHCGA/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'math',
    languages: ['en'],
    age_range: 'kids',
    duration: 60,
    family_safe: true,
    tags: ['numbers', 'counting', 'toddlers', 'songs'],
  },
  {
    id: 'kids-il-etait-vie',
    title: { en: 'Once Upon a Time... Life', fr: 'Il etait une fois... la Vie', ar: '\u0643\u0627\u0646 \u064A\u0627 \u0645\u0627 \u0643\u0627\u0646... \u0627\u0644\u062D\u064A\u0627\u0629', es: 'Erase una vez... la Vida', de: 'Es war einmal... das Leben' },
    description: { en: 'The classic animated show about the human body.', fr: 'Le dessin anime culte sur le corps humain.', ar: '\u0627\u0644\u0645\u0633\u0644\u0633\u0644 \u0627\u0644\u0643\u0644\u0627\u0633\u064A\u0643\u064A \u0639\u0646 \u062C\u0633\u0645 \u0627\u0644\u0625\u0646\u0633\u0627\u0646.', es: 'La clasica serie animada sobre el cuerpo humano.', de: 'Die klassische Zeichentrickserie uber den menschlichen Korper.' },
    url: 'https://www.youtube.com/watch?v=Fk5LMRUMPFI',
    embed_url: 'https://www.youtube-nocookie.com/embed/Fk5LMRUMPFI',
    thumbnail: 'https://img.youtube.com/vi/Fk5LMRUMPFI/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'science',
    subcategory: 'biology',
    languages: ['fr', 'en', 'es', 'de', 'ar'],
    age_range: 'kids',
    duration: 25,
    family_safe: true,
    featured: true,
    tags: ['human body', 'biology', 'classic', 'animation'],
  },

  // ====================== PODCASTS ======================

  {
    id: 'pod-stuff-you-should-know',
    title: { en: 'Stuff You Should Know', fr: 'Stuff You Should Know', ar: '\u0623\u0634\u064A\u0627\u0621 \u064A\u062C\u0628 \u0623\u0646 \u062A\u0639\u0631\u0641\u0647\u0627', es: 'Cosas que Deberias Saber', de: 'Dinge die man wissen sollte' },
    description: { en: 'A popular podcast exploring how things work.', fr: 'Un podcast populaire explorant le fonctionnement des choses.', ar: '\u0628\u0648\u062F\u0643\u0627\u0633\u062A \u0634\u0647\u064A\u0631 \u064A\u0633\u062A\u0643\u0634\u0641 \u0643\u064A\u0641 \u062A\u0639\u0645\u0644 \u0627\u0644\u0623\u0634\u064A\u0627\u0621.', es: 'Un podcast popular que explora como funcionan las cosas.', de: 'Ein beliebter Podcast der erklart wie Dinge funktionieren.' },
    url: 'https://www.iheart.com/podcast/105-stuff-you-should-know-26940277/',
    source: 'Apple Podcasts',
    type: 'podcast',
    category: 'science',
    languages: ['en'],
    age_range: 'all',
    duration: 50,
    family_safe: true,
    tags: ['general knowledge', 'how things work'],
  },
  {
    id: 'pod-radiolab',
    title: { en: 'Radiolab', fr: 'Radiolab', ar: 'Radiolab', es: 'Radiolab', de: 'Radiolab' },
    description: { en: 'Investigating big questions about science, philosophy, and human experience.', fr: 'Investigation de grandes questions sur la science et la philosophie.', ar: '\u0627\u0633\u062A\u0643\u0634\u0627\u0641 \u0623\u0633\u0626\u0644\u0629 \u0643\u0628\u064A\u0631\u0629 \u062D\u0648\u0644 \u0627\u0644\u0639\u0644\u0645 \u0648\u0627\u0644\u0641\u0644\u0633\u0641\u0629.', es: 'Investigando grandes preguntas sobre ciencia y filosofia.', de: 'Erforschung grosser Fragen uber Wissenschaft und Philosophie.' },
    url: 'https://radiolab.org/',
    source: 'NPR',
    type: 'podcast',
    category: 'science',
    languages: ['en'],
    age_range: 'all',
    duration: 45,
    family_safe: true,
    tags: ['science', 'philosophy', 'storytelling'],
  },
  {
    id: 'pod-ted-radio-hour',
    title: { en: 'TED Radio Hour', fr: 'TED Radio Hour', ar: '\u0633\u0627\u0639\u0629 \u0631\u0627\u062F\u064A\u0648 TED', es: 'TED Radio Hour', de: 'TED Radio Hour' },
    description: { en: 'Exploring the biggest ideas of our time through TED Talks.', fr: 'Explorer les plus grandes idees de notre temps.', ar: '\u0627\u0633\u062A\u0643\u0634\u0627\u0641 \u0623\u0643\u0628\u0631 \u0623\u0641\u0643\u0627\u0631 \u0639\u0635\u0631\u0646\u0627 \u0639\u0628\u0631 \u0645\u062D\u0627\u062F\u062B\u0627\u062A TED.', es: 'Explorando las grandes ideas de nuestro tiempo.', de: 'Die grossten Ideen unserer Zeit durch TED Talks erkunden.' },
    url: 'https://www.npr.org/programs/ted-radio-hour/',
    source: 'NPR',
    type: 'podcast',
    category: 'science',
    languages: ['en'],
    age_range: 'all',
    duration: 55,
    family_safe: true,
    featured: true,
    tags: ['ted', 'ideas', 'innovation'],
  },
  {
    id: 'pod-france-inter-tete',
    title: { en: 'La Tete au Carre', fr: 'La Tete au Carre', ar: '\u0631\u0623\u0633 \u0645\u0631\u0628\u0639', es: 'La Tete au Carre', de: 'La Tete au Carre' },
    description: { en: 'France Inter\'s popular science podcast.', fr: 'Le podcast de science populaire de France Inter.', ar: '\u0628\u0648\u062F\u0643\u0627\u0633\u062A \u0627\u0644\u0639\u0644\u0648\u0645 \u0627\u0644\u0634\u0647\u064A\u0631 \u0645\u0646 France Inter.', es: 'El podcast de ciencia popular de France Inter.', de: 'France Inters popularer Wissenschaftspodcast.' },
    url: 'https://www.radiofrance.fr/franceinter/podcasts/la-terre-au-carre',
    source: 'France Inter',
    type: 'podcast',
    category: 'science',
    languages: ['fr'],
    age_range: 'all',
    duration: 55,
    family_safe: true,
    tags: ['science', 'french', 'radio'],
  },
  {
    id: 'pod-arabic-scitech',
    title: { en: 'Arabic Science & Technology Podcast', fr: 'Podcast arabe Science & Technologie', ar: '\u0628\u0648\u062F\u0643\u0627\u0633\u062A \u0627\u0644\u0639\u0644\u0648\u0645 \u0648\u0627\u0644\u062A\u0643\u0646\u0648\u0644\u0648\u062C\u064A\u0627', es: 'Podcast arabe Ciencia y Tecnologia', de: 'Arabischer Wissenschafts- und Technologiepodcast' },
    description: { en: 'Exploring science and technology in Arabic.', fr: 'Explorer la science et la technologie en arabe.', ar: '\u0627\u0633\u062A\u0643\u0634\u0627\u0641 \u0627\u0644\u0639\u0644\u0648\u0645 \u0648\u0627\u0644\u062A\u0643\u0646\u0648\u0644\u0648\u062C\u064A\u0627 \u0628\u0627\u0644\u0639\u0631\u0628\u064A\u0629.', es: 'Explorando ciencia y tecnologia en arabe.', de: 'Wissenschaft und Technologie auf Arabisch erkunden.' },
    url: 'https://www.youtube.com/watch?v=abcscience',
    source: 'YouTube',
    type: 'podcast',
    category: 'technology',
    languages: ['ar'],
    age_range: 'all',
    duration: 40,
    family_safe: true,
    tags: ['science', 'technology', 'arabic'],
  },
  {
    id: 'pod-brains-on',
    title: { en: 'Brains On! Science Podcast', fr: 'Brains On! Podcast Science', ar: 'Brains On! \u0628\u0648\u062F\u0643\u0627\u0633\u062A \u0627\u0644\u0639\u0644\u0648\u0645', es: 'Brains On! Podcast Ciencia', de: 'Brains On! Wissenschaftspodcast' },
    description: { en: 'Award-winning science podcast for kids.', fr: 'Podcast de science primé pour enfants.', ar: '\u0628\u0648\u062F\u0643\u0627\u0633\u062A \u0639\u0644\u0645\u064A \u062D\u0627\u0626\u0632 \u0639\u0644\u0649 \u062C\u0648\u0627\u0626\u0632 \u0644\u0644\u0623\u0637\u0641\u0627\u0644.', es: 'Podcast de ciencia premiado para ninos.', de: 'Preisgekronter Wissenschaftspodcast fur Kinder.' },
    url: 'https://www.brainson.org/',
    source: 'NPR',
    type: 'podcast',
    category: 'science',
    languages: ['en'],
    age_range: 'kids',
    duration: 30,
    family_safe: true,
    tags: ['kids', 'science', 'podcast'],
  },
  {
    id: 'pod-history-extra',
    title: { en: 'History Extra Podcast', fr: 'History Extra Podcast', ar: '\u0628\u0648\u062F\u0643\u0627\u0633\u062A \u0627\u0644\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0625\u0636\u0627\u0641\u064A', es: 'History Extra Podcast', de: 'History Extra Podcast' },
    description: { en: 'BBC History Magazine\'s podcast covering fascinating historical topics.', fr: 'Le podcast du magazine BBC History.', ar: '\u0628\u0648\u062F\u0643\u0627\u0633\u062A \u0645\u062C\u0644\u0629 BBC History.', es: 'El podcast de la revista BBC History.', de: 'Der Podcast des BBC History Magazins.' },
    url: 'https://www.historyextra.com/podcast/',
    source: 'BBC',
    type: 'podcast',
    category: 'history',
    languages: ['en'],
    age_range: 'all',
    duration: 40,
    family_safe: true,
    tags: ['history', 'bbc', 'stories'],
  },
  {
    id: 'pod-rfi-journal',
    title: { en: 'RFI - Easy French Journal', fr: 'RFI - Journal en francais facile', ar: 'RFI - \u0627\u0644\u064A\u0648\u0645\u064A\u0629 \u0628\u0627\u0644\u0641\u0631\u0646\u0633\u064A\u0629 \u0627\u0644\u0633\u0647\u0644\u0629', es: 'RFI - Diario en frances facil', de: 'RFI - Leichtes Franzosisch Journal' },
    description: { en: 'Daily news in simplified French - great for learners.', fr: 'Actualites quotidiennes en francais simplifie.', ar: '\u0623\u062E\u0628\u0627\u0631 \u064A\u0648\u0645\u064A\u0629 \u0628\u0627\u0644\u0641\u0631\u0646\u0633\u064A\u0629 \u0627\u0644\u0645\u0628\u0633\u0637\u0629.', es: 'Noticias diarias en frances simplificado.', de: 'Tagliche Nachrichten in vereinfachtem Franzosisch.' },
    url: 'https://savoirs.rfi.fr/fr/apprendre-enseigner/langue-francaise/journal-en-francais-facile',
    source: 'RFI',
    type: 'podcast',
    category: 'news',
    languages: ['fr'],
    age_range: 'all',
    duration: 10,
    family_safe: true,
    tags: ['french', 'news', 'language learning'],
  },

  // ====================== LIVE TV / RADIO ======================

  {
    id: 'live-dw-en',
    title: { en: 'DW News Live', fr: 'DW News en direct', ar: 'DW News \u0645\u0628\u0627\u0634\u0631', es: 'DW News en vivo', de: 'DW News Live' },
    description: { en: 'Deutsche Welle 24/7 news in English.', fr: 'Deutsche Welle infos 24/7 en anglais.', ar: '\u0623\u062E\u0628\u0627\u0631 Deutsche Welle 24/7 \u0628\u0627\u0644\u0625\u0646\u062C\u0644\u064A\u0632\u064A\u0629.', es: 'Noticias Deutsche Welle 24/7 en ingles.', de: 'Deutsche Welle 24/7 Nachrichten auf Englisch.' },
    url: 'https://www.youtube.com/watch?v=GE_SfNVNyqk',
    embed_url: 'https://www.youtube-nocookie.com/embed/GE_SfNVNyqk',
    thumbnail: 'https://img.youtube.com/vi/GE_SfNVNyqk/hqdefault.jpg',
    source: 'DW',
    type: 'live_tv',
    category: 'news',
    languages: ['en'],
    age_range: 'all',
    family_safe: true,
    tags: ['news', 'live', 'english'],
  },
  {
    id: 'live-france24-fr',
    title: { en: 'France 24 Live (French)', fr: 'France 24 en direct', ar: 'France 24 \u0645\u0628\u0627\u0634\u0631 (\u0641\u0631\u0646\u0633\u064A)', es: 'France 24 en vivo (Frances)', de: 'France 24 Live (Franzosisch)' },
    description: { en: 'French 24/7 news channel live stream.', fr: 'La chaine info en continu France 24.', ar: '\u0642\u0646\u0627\u0629 \u0627\u0644\u0623\u062E\u0628\u0627\u0631 \u0627\u0644\u0641\u0631\u0646\u0633\u064A\u0629 \u0645\u0628\u0627\u0634\u0631\u0629.', es: 'Canal de noticias frances en directo.', de: 'Franzosischer 24/7 Nachrichtensender.' },
    url: 'https://www.youtube.com/watch?v=l8PMl7tUDIE',
    embed_url: 'https://www.youtube-nocookie.com/embed/l8PMl7tUDIE',
    thumbnail: 'https://img.youtube.com/vi/l8PMl7tUDIE/hqdefault.jpg',
    source: 'France 24',
    type: 'live_tv',
    category: 'news',
    languages: ['fr'],
    age_range: 'all',
    family_safe: true,
    tags: ['news', 'live', 'french'],
  },
  {
    id: 'live-aljazeera-ar',
    title: { en: 'Al Jazeera Arabic Live', fr: 'Al Jazeera Arabe en direct', ar: '\u0627\u0644\u062C\u0632\u064A\u0631\u0629 \u0645\u0628\u0627\u0634\u0631', es: 'Al Jazeera Arabe en vivo', de: 'Al Jazeera Arabisch Live' },
    description: { en: 'Al Jazeera Arabic 24/7 news.', fr: 'Al Jazeera en arabe 24/7.', ar: '\u0627\u0644\u062C\u0632\u064A\u0631\u0629 \u0628\u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0645\u0628\u0627\u0634\u0631 24/7.', es: 'Al Jazeera en arabe 24/7.', de: 'Al Jazeera auf Arabisch 24/7.' },
    url: 'https://www.youtube.com/watch?v=bNyUyrR0PHo',
    embed_url: 'https://www.youtube-nocookie.com/embed/bNyUyrR0PHo',
    thumbnail: 'https://img.youtube.com/vi/bNyUyrR0PHo/hqdefault.jpg',
    source: 'Al Jazeera',
    type: 'live_tv',
    category: 'news',
    languages: ['ar'],
    age_range: 'all',
    family_safe: true,
    tags: ['news', 'live', 'arabic'],
  },
  {
    id: 'live-aljazeera-en',
    title: { en: 'Al Jazeera English Live', fr: 'Al Jazeera Anglais en direct', ar: '\u0627\u0644\u062C\u0632\u064A\u0631\u0629 \u0627\u0644\u0625\u0646\u062C\u0644\u064A\u0632\u064A\u0629 \u0645\u0628\u0627\u0634\u0631', es: 'Al Jazeera Ingles en vivo', de: 'Al Jazeera Englisch Live' },
    description: { en: 'Al Jazeera English 24/7 live news stream.', fr: 'Al Jazeera en anglais en direct 24/7.', ar: '\u0627\u0644\u062C\u0632\u064A\u0631\u0629 \u0628\u0627\u0644\u0625\u0646\u062C\u0644\u064A\u0632\u064A\u0629 \u0645\u0628\u0627\u0634\u0631 24/7.', es: 'Al Jazeera en ingles en directo 24/7.', de: 'Al Jazeera auf Englisch Live 24/7.' },
    url: 'https://www.youtube.com/watch?v=F-POY4Q0QSI',
    embed_url: 'https://www.youtube-nocookie.com/embed/F-POY4Q0QSI',
    thumbnail: 'https://img.youtube.com/vi/F-POY4Q0QSI/hqdefault.jpg',
    source: 'Al Jazeera',
    type: 'live_tv',
    category: 'news',
    languages: ['en'],
    age_range: 'all',
    family_safe: true,
    tags: ['news', 'live', 'english'],
  },
  {
    id: 'live-euronews-multi',
    title: { en: 'Euronews Live', fr: 'Euronews en direct', ar: 'Euronews \u0645\u0628\u0627\u0634\u0631', es: 'Euronews en vivo', de: 'Euronews Live' },
    description: { en: 'European news in multiple languages.', fr: 'Actualites europeennes en plusieurs langues.', ar: '\u0623\u062E\u0628\u0627\u0631 \u0623\u0648\u0631\u0648\u0628\u064A\u0629 \u0628\u0639\u062F\u0629 \u0644\u063A\u0627\u062A.', es: 'Noticias europeas en varios idiomas.', de: 'Europaische Nachrichten in mehreren Sprachen.' },
    url: 'https://www.youtube.com/watch?v=jL8uDJJBjMA',
    embed_url: 'https://www.youtube-nocookie.com/embed/jL8uDJJBjMA',
    thumbnail: 'https://img.youtube.com/vi/jL8uDJJBjMA/hqdefault.jpg',
    source: 'Euronews',
    type: 'live_tv',
    category: 'news',
    languages: ['en', 'fr', 'de', 'es', 'ar'],
    age_range: 'all',
    family_safe: true,
    tags: ['news', 'live', 'europe', 'multilingual'],
  },
  {
    id: 'live-bbc-world-radio',
    title: { en: 'BBC World Service Radio', fr: 'BBC World Service Radio', ar: '\u0631\u0627\u062F\u064A\u0648 BBC \u0627\u0644\u0639\u0627\u0644\u0645\u064A', es: 'BBC World Service Radio', de: 'BBC World Service Radio' },
    description: { en: 'BBC World Service radio live stream.', fr: 'Radio BBC World Service en direct.', ar: '\u0631\u0627\u062F\u064A\u0648 BBC World Service \u0645\u0628\u0627\u0634\u0631.', es: 'BBC World Service radio en directo.', de: 'BBC World Service Radio Live-Stream.' },
    url: 'https://www.bbc.co.uk/sounds/play/live:bbc_world_service',
    source: 'BBC',
    type: 'live_radio',
    category: 'news',
    languages: ['en'],
    age_range: 'all',
    family_safe: true,
    embeddable: false,
    tags: ['radio', 'news', 'bbc', 'live'],
  },
  {
    id: 'live-france-inter',
    title: { en: 'France Inter Radio Live', fr: 'France Inter en direct', ar: '\u0631\u0627\u062F\u064A\u0648 France Inter \u0645\u0628\u0627\u0634\u0631', es: 'France Inter Radio en vivo', de: 'France Inter Radio Live' },
    description: { en: 'France\'s most popular radio station live.', fr: 'La radio la plus ecoutee de France en direct.', ar: '\u0623\u0634\u0647\u0631 \u0645\u062D\u0637\u0629 \u0631\u0627\u062F\u064A\u0648 \u0641\u0631\u0646\u0633\u064A\u0629 \u0645\u0628\u0627\u0634\u0631\u0629.', es: 'La radio mas popular de Francia en directo.', de: 'Frankreichs beliebtester Radiosender live.' },
    url: 'https://www.radiofrance.fr/franceinter/direct',
    source: 'France Inter',
    type: 'live_radio',
    category: 'news',
    languages: ['fr'],
    age_range: 'all',
    family_safe: true,
    embeddable: false,
    tags: ['radio', 'french', 'live', 'culture'],
  },
  {
    id: 'live-rfi-radio',
    title: { en: 'RFI Radio Live', fr: 'RFI en direct', ar: '\u0631\u0627\u062F\u064A\u0648 RFI \u0645\u0628\u0627\u0634\u0631', es: 'RFI Radio en vivo', de: 'RFI Radio Live' },
    description: { en: 'Radio France Internationale live stream.', fr: 'Radio France Internationale en direct.', ar: 'Radio France Internationale \u0645\u0628\u0627\u0634\u0631.', es: 'Radio France Internationale en directo.', de: 'Radio France Internationale Live-Stream.' },
    url: 'https://www.rfi.fr/en/live',
    source: 'RFI',
    type: 'live_radio',
    category: 'news',
    languages: ['fr', 'en', 'ar', 'es'],
    age_range: 'all',
    family_safe: true,
    tags: ['radio', 'international', 'live', 'news'],
  },
  {
    id: 'live-npr-radio',
    title: { en: 'NPR Radio Live', fr: 'NPR Radio en direct', ar: '\u0631\u0627\u062F\u064A\u0648 NPR \u0645\u0628\u0627\u0634\u0631', es: 'NPR Radio en vivo', de: 'NPR Radio Live' },
    description: { en: 'National Public Radio live stream.', fr: 'National Public Radio en direct.', ar: '\u0627\u0644\u0625\u0630\u0627\u0639\u0629 \u0627\u0644\u0639\u0627\u0645\u0629 \u0627\u0644\u0648\u0637\u0646\u064A\u0629 \u0645\u0628\u0627\u0634\u0631.', es: 'National Public Radio en directo.', de: 'National Public Radio Live-Stream.' },
    url: 'https://www.npr.org/live-updates',
    source: 'NPR',
    type: 'live_radio',
    category: 'news',
    languages: ['en'],
    age_range: 'all',
    family_safe: true,
    embeddable: false,
    tags: ['radio', 'news', 'american', 'live'],
  },
  {
    id: 'live-dw-de',
    title: { en: 'DW News Live (German)', fr: 'DW News en direct (Allemand)', ar: 'DW News \u0645\u0628\u0627\u0634\u0631 (\u0623\u0644\u0645\u0627\u0646\u064A)', es: 'DW News en vivo (Aleman)', de: 'DW News Live (Deutsch)' },
    description: { en: 'Deutsche Welle 24/7 news in German.', fr: 'Deutsche Welle en allemand 24/7.', ar: '\u0623\u062E\u0628\u0627\u0631 Deutsche Welle 24/7 \u0628\u0627\u0644\u0623\u0644\u0645\u0627\u0646\u064A\u0629.', es: 'Noticias Deutsche Welle 24/7 en aleman.', de: 'Deutsche Welle 24/7 Nachrichten auf Deutsch.' },
    url: 'https://www.youtube.com/watch?v=PSJ30BjKh54',
    embed_url: 'https://www.youtube-nocookie.com/embed/PSJ30BjKh54',
    thumbnail: 'https://img.youtube.com/vi/PSJ30BjKh54/hqdefault.jpg',
    source: 'DW',
    type: 'live_tv',
    category: 'news',
    languages: ['de'],
    age_range: 'all',
    family_safe: true,
    tags: ['news', 'live', 'german'],
  },

  // ====================== ISLAMIC ======================

  {
    id: 'isl-alafasy-quran',
    title: { en: 'Quran Recitation - Mishary Rashid Alafasy', fr: 'Recitation du Coran - Mishary Rashid Alafasy', ar: '\u062A\u0644\u0627\u0648\u0629 \u0627\u0644\u0642\u0631\u0622\u0646 - \u0645\u0634\u0627\u0631\u064A \u0631\u0627\u0634\u062F \u0627\u0644\u0639\u0641\u0627\u0633\u064A', es: 'Recitacion del Coran - Mishary Rashid Alafasy', de: 'Koran-Rezitation - Mishary Rashid Alafasy' },
    description: { en: 'Beautiful Quran recitation by Sheikh Mishary Rashid Alafasy.', fr: 'Belle recitation du Coran par Sheikh Mishary Rashid Alafasy.', ar: '\u062A\u0644\u0627\u0648\u0629 \u062C\u0645\u064A\u0644\u0629 \u0644\u0644\u0642\u0631\u0622\u0646 \u0627\u0644\u0643\u0631\u064A\u0645 \u0628\u0635\u0648\u062A \u0627\u0644\u0634\u064A\u062E \u0645\u0634\u0627\u0631\u064A \u0631\u0627\u0634\u062F \u0627\u0644\u0639\u0641\u0627\u0633\u064A.', es: 'Hermosa recitacion del Coran por Sheikh Mishary Rashid Alafasy.', de: 'Schone Koran-Rezitation von Sheikh Mishary Rashid Alafasy.' },
    url: 'https://www.youtube.com/watch?v=PMmBr5RdWLI',
    embed_url: 'https://www.youtube-nocookie.com/embed/PMmBr5RdWLI',
    thumbnail: 'https://img.youtube.com/vi/PMmBr5RdWLI/hqdefault.jpg',
    source: 'YouTube',
    type: 'channel',
    category: 'quran',
    languages: ['ar'],
    age_range: 'all',
    family_safe: true,
    featured: true,
    tags: ['quran', 'recitation', 'alafasy'],
  },
  {
    id: 'isl-bayyinah-nouman',
    title: { en: 'Bayyinah - Nouman Ali Khan', fr: 'Bayyinah - Nouman Ali Khan', ar: '\u0628\u064A\u0651\u0646\u0629 - \u0646\u0639\u0645\u0627\u0646 \u0639\u0644\u064A \u062E\u0627\u0646', es: 'Bayyinah - Nouman Ali Khan', de: 'Bayyinah - Nouman Ali Khan' },
    description: { en: 'Quran and Arabic linguistic analysis with Nouman Ali Khan.', fr: "Analyse linguistique du Coran avec Nouman Ali Khan.", ar: '\u062A\u062D\u0644\u064A\u0644 \u0644\u063A\u0648\u064A \u0644\u0644\u0642\u0631\u0622\u0646 \u0645\u0639 \u0646\u0639\u0645\u0627\u0646 \u0639\u0644\u064A \u062E\u0627\u0646.', es: 'Analisis linguistico del Coran con Nouman Ali Khan.', de: 'Linguistische Analyse des Korans mit Nouman Ali Khan.' },
    url: 'https://www.youtube.com/watch?v=EjPr4ZkIjLc',
    embed_url: 'https://www.youtube-nocookie.com/embed/EjPr4ZkIjLc',
    thumbnail: 'https://img.youtube.com/vi/EjPr4ZkIjLc/hqdefault.jpg',
    source: 'YouTube',
    type: 'channel',
    category: 'islamic',
    languages: ['en', 'ar'],
    age_range: 'all',
    family_safe: true,
    tags: ['quran', 'arabic', 'tafsir', 'education'],
  },
  {
    id: 'isl-yaqeen-institute',
    title: { en: 'Yaqeen Institute', fr: 'Institut Yaqeen', ar: '\u0645\u0639\u0647\u062F \u064A\u0642\u064A\u0646', es: 'Instituto Yaqeen', de: 'Yaqeen Institut' },
    description: { en: 'Research-based Islamic content addressing modern questions.', fr: 'Contenu islamique base sur la recherche.', ar: '\u0645\u062D\u062A\u0648\u0649 \u0625\u0633\u0644\u0627\u0645\u064A \u0642\u0627\u0626\u0645 \u0639\u0644\u0649 \u0627\u0644\u0628\u062D\u062B \u064A\u062A\u0646\u0627\u0648\u0644 \u0623\u0633\u0626\u0644\u0629 \u0645\u0639\u0627\u0635\u0631\u0629.', es: 'Contenido islamico basado en investigacion.', de: 'Forschungsbasierter islamischer Inhalt.' },
    url: 'https://www.youtube.com/watch?v=4Q5Zp0ER-d8',
    embed_url: 'https://www.youtube-nocookie.com/embed/4Q5Zp0ER-d8',
    thumbnail: 'https://img.youtube.com/vi/4Q5Zp0ER-d8/hqdefault.jpg',
    source: 'YouTube',
    type: 'channel',
    category: 'islamic',
    languages: ['en'],
    age_range: 'all',
    family_safe: true,
    tags: ['islamic', 'research', 'modern', 'faith'],
  },
  {
    id: 'isl-omar-suleiman',
    title: { en: 'Omar Suleiman - Angels in Your Presence', fr: 'Omar Suleiman - Les Anges a vos cotes', ar: '\u0639\u0645\u0631 \u0633\u0644\u064A\u0645\u0627\u0646 - \u0627\u0644\u0645\u0644\u0627\u0626\u0643\u0629 \u0628\u062C\u0627\u0646\u0628\u0643', es: 'Omar Suleiman - Angeles a tu lado', de: 'Omar Suleiman - Engel an Ihrer Seite' },
    description: { en: 'A series about the angels mentioned in Islamic tradition.', fr: 'Une serie sur les anges dans la tradition islamique.', ar: '\u0633\u0644\u0633\u0644\u0629 \u0639\u0646 \u0627\u0644\u0645\u0644\u0627\u0626\u0643\u0629 \u0641\u064A \u0627\u0644\u062A\u0642\u0644\u064A\u062F \u0627\u0644\u0625\u0633\u0644\u0627\u0645\u064A.', es: 'Una serie sobre los angeles en la tradicion islamica.', de: 'Eine Serie uber die Engel in der islamischen Tradition.' },
    url: 'https://www.youtube.com/watch?v=K7xyVQHRC4o',
    embed_url: 'https://www.youtube-nocookie.com/embed/K7xyVQHRC4o',
    thumbnail: 'https://img.youtube.com/vi/K7xyVQHRC4o/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'islamic',
    languages: ['en'],
    age_range: 'all',
    duration: 10,
    family_safe: true,
    tags: ['islamic', 'angels', 'spirituality'],
  },
  {
    id: 'isl-mufti-menk',
    title: { en: 'Mufti Menk - Boost Your Faith', fr: 'Mufti Menk - Renforcez votre foi', ar: '\u0645\u0641\u062A\u064A \u0645\u0646\u0643 - \u0639\u0632\u0632 \u0625\u064A\u0645\u0627\u0646\u0643', es: 'Mufti Menk - Fortalece tu Fe', de: 'Mufti Menk - Starke deinen Glauben' },
    description: { en: 'Inspirational lectures by Mufti Ismail Menk.', fr: 'Conferences inspirantes de Mufti Ismail Menk.', ar: '\u0645\u062D\u0627\u0636\u0631\u0627\u062A \u0645\u0644\u0647\u0645\u0629 \u0644\u0644\u0645\u0641\u062A\u064A \u0625\u0633\u0645\u0627\u0639\u064A\u0644 \u0645\u0646\u0643.', es: 'Conferencias inspiradoras del Mufti Ismail Menk.', de: 'Inspirierende Vortrage von Mufti Ismail Menk.' },
    url: 'https://www.youtube.com/watch?v=U-HCf5gD5CE',
    embed_url: 'https://www.youtube-nocookie.com/embed/U-HCf5gD5CE',
    thumbnail: 'https://img.youtube.com/vi/U-HCf5gD5CE/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'islamic',
    languages: ['en'],
    age_range: 'all',
    duration: 15,
    family_safe: true,
    tags: ['islamic', 'motivation', 'faith'],
  },
  {
    id: 'isl-quran-fr',
    title: { en: 'Quran with French Translation', fr: 'Coran avec traduction francaise', ar: '\u0627\u0644\u0642\u0631\u0622\u0646 \u0645\u0639 \u062A\u0631\u062C\u0645\u0629 \u0641\u0631\u0646\u0633\u064A\u0629', es: 'Coran con traduccion francesa', de: 'Koran mit franzosischer Ubersetzung' },
    description: { en: 'Quran recitation with French subtitles.', fr: 'Recitation du Coran avec sous-titres francais.', ar: '\u062A\u0644\u0627\u0648\u0629 \u0627\u0644\u0642\u0631\u0622\u0646 \u0645\u0639 \u062A\u0631\u062C\u0645\u0629 \u0641\u0631\u0646\u0633\u064A\u0629.', es: 'Recitacion del Coran con subtitulos en frances.', de: 'Koran-Rezitation mit franzosischen Untertiteln.' },
    url: 'https://www.youtube.com/watch?v=9lOaLO0FW_A',
    embed_url: 'https://www.youtube-nocookie.com/embed/9lOaLO0FW_A',
    thumbnail: 'https://img.youtube.com/vi/9lOaLO0FW_A/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'quran',
    languages: ['ar', 'fr'],
    age_range: 'all',
    duration: 120,
    family_safe: true,
    tags: ['quran', 'french', 'translation', 'recitation'],
  },
  {
    id: 'isl-islamic-lectures-fr',
    title: { en: 'Islamic Lectures in French', fr: 'Conferences islamiques en francais', ar: '\u0645\u062D\u0627\u0636\u0631\u0627\u062A \u0625\u0633\u0644\u0627\u0645\u064A\u0629 \u0628\u0627\u0644\u0641\u0631\u0646\u0633\u064A\u0629', es: 'Conferencias islamicas en frances', de: 'Islamische Vortrage auf Franzosisch' },
    description: { en: 'Islamic education content in French.', fr: 'Contenu educatif islamique en francais.', ar: '\u0645\u062D\u062A\u0648\u0649 \u062A\u0639\u0644\u064A\u0645\u064A \u0625\u0633\u0644\u0627\u0645\u064A \u0628\u0627\u0644\u0641\u0631\u0646\u0633\u064A\u0629.', es: 'Contenido educativo islamico en frances.', de: 'Islamischer Bildungsinhalt auf Franzosisch.' },
    url: 'https://www.youtube.com/results?search_query=conference+islamique+francais',
    source: 'YouTube',
    type: 'channel',
    category: 'islamic',
    languages: ['fr'],
    age_range: 'all',
    family_safe: true,
    embeddable: false,
    tags: ['islamic', 'french', 'lectures', 'education'],
  },
  {
    id: 'isl-quran-kids',
    title: { en: 'Quran for Kids - Juz Amma', fr: 'Coran pour enfants - Juz Amma', ar: '\u0627\u0644\u0642\u0631\u0622\u0646 \u0644\u0644\u0623\u0637\u0641\u0627\u0644 - \u062C\u0632\u0621 \u0639\u0645', es: 'Coran para Ninos - Juz Amma', de: 'Koran fur Kinder - Juz Amma' },
    description: { en: 'Learn Juz Amma with beautiful recitation for children.', fr: 'Apprendre Juz Amma avec une belle recitation pour enfants.', ar: '\u062A\u0639\u0644\u0645 \u062C\u0632\u0621 \u0639\u0645 \u0628\u062A\u0644\u0627\u0648\u0629 \u062C\u0645\u064A\u0644\u0629 \u0644\u0644\u0623\u0637\u0641\u0627\u0644.', es: 'Aprende Juz Amma con hermosa recitacion para ninos.', de: 'Juz Amma mit schoner Rezitation fur Kinder lernen.' },
    url: 'https://www.youtube.com/watch?v=qH-kH10fsXc',
    embed_url: 'https://www.youtube-nocookie.com/embed/qH-kH10fsXc',
    thumbnail: 'https://img.youtube.com/vi/qH-kH10fsXc/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'quran',
    languages: ['ar'],
    age_range: 'kids',
    duration: 60,
    family_safe: true,
    tags: ['quran', 'kids', 'juz amma', 'memorization'],
  },
  {
    id: 'isl-islamic-history-ar',
    title: { en: 'Islamic History - Arabic Series', fr: 'Histoire islamique - Serie arabe', ar: '\u0627\u0644\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0625\u0633\u0644\u0627\u0645\u064A - \u0633\u0644\u0633\u0644\u0629 \u0639\u0631\u0628\u064A\u0629', es: 'Historia Islamica - Serie arabe', de: 'Islamische Geschichte - Arabische Serie' },
    description: { en: 'The history of Islamic civilization in Arabic.', fr: "L'histoire de la civilisation islamique en arabe.", ar: '\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u062D\u0636\u0627\u0631\u0629 \u0627\u0644\u0625\u0633\u0644\u0627\u0645\u064A\u0629 \u0628\u0627\u0644\u0639\u0631\u0628\u064A\u0629.', es: 'La historia de la civilizacion islamica en arabe.', de: 'Die Geschichte der islamischen Zivilisation auf Arabisch.' },
    url: 'https://www.youtube.com/watch?v=7_GYwL7gkUc',
    embed_url: 'https://www.youtube-nocookie.com/embed/7_GYwL7gkUc',
    thumbnail: 'https://img.youtube.com/vi/7_GYwL7gkUc/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'islamic',
    subcategory: 'history',
    languages: ['ar'],
    age_range: 'all',
    duration: 45,
    family_safe: true,
    tags: ['islamic history', 'arabic', 'civilization'],
  },
  {
    id: 'isl-mercifulservant',
    title: { en: 'The Merciful Servant - Inspirational', fr: 'The Merciful Servant - Inspirant', ar: '\u0627\u0644\u062E\u0627\u062F\u0645 \u0627\u0644\u0631\u062D\u064A\u0645 - \u0645\u0644\u0647\u0645', es: 'The Merciful Servant - Inspiracional', de: 'The Merciful Servant - Inspirierend' },
    description: { en: 'Inspirational Islamic reminders and stories.', fr: 'Rappels et histoires islamiques inspirants.', ar: '\u062A\u0630\u0643\u064A\u0631\u0627\u062A \u0648\u0642\u0635\u0635 \u0625\u0633\u0644\u0627\u0645\u064A\u0629 \u0645\u0644\u0647\u0645\u0629.', es: 'Recordatorios e historias islamicas inspiradoras.', de: 'Inspirierende islamische Erinnerungen und Geschichten.' },
    url: 'https://www.youtube.com/watch?v=8m8HnwlH7Xk',
    embed_url: 'https://www.youtube-nocookie.com/embed/8m8HnwlH7Xk',
    thumbnail: 'https://img.youtube.com/vi/8m8HnwlH7Xk/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'islamic',
    languages: ['en'],
    age_range: 'all',
    duration: 10,
    family_safe: true,
    tags: ['islamic', 'motivation', 'stories', 'reminders'],
  },

  // ====================== ADDITIONAL DOCUMENTARIES ======================

  {
    id: 'doc-dw-amazon',
    title: { en: 'The Amazon Rainforest', fr: 'La Foret amazonienne', ar: '\u063A\u0627\u0628\u0627\u062A \u0627\u0644\u0623\u0645\u0627\u0632\u0648\u0646', es: 'La Selva Amazonica', de: 'Der Amazonas-Regenwald' },
    description: { en: 'Exploring the world\'s largest tropical rainforest.', fr: 'Exploration de la plus grande foret tropicale du monde.', ar: '\u0627\u0633\u062A\u0643\u0634\u0627\u0641 \u0623\u0643\u0628\u0631 \u063A\u0627\u0628\u0629 \u0627\u0633\u062A\u0648\u0627\u0626\u064A\u0629 \u0641\u064A \u0627\u0644\u0639\u0627\u0644\u0645.', es: 'Explorando la selva tropical mas grande del mundo.', de: 'Erforschung des grossten tropischen Regenwaldes der Welt.' },
    url: 'https://www.youtube.com/watch?v=7s5Np5dCM3E',
    embed_url: 'https://www.youtube-nocookie.com/embed/7s5Np5dCM3E',
    thumbnail: 'https://img.youtube.com/vi/7s5Np5dCM3E/hqdefault.jpg',
    source: 'DW',
    type: 'documentary',
    category: 'nature',
    languages: ['en', 'de', 'es'],
    age_range: 'all',
    duration: 42,
    family_safe: true,
    tags: ['amazon', 'rainforest', 'nature', 'environment'],
  },
  {
    id: 'doc-kurzgesagt-universe',
    title: { en: 'The Egg - A Short Story', fr: 'L\'Oeuf - Une Courte Histoire', ar: '\u0627\u0644\u0628\u064A\u0636\u0629 - \u0642\u0635\u0629 \u0642\u0635\u064A\u0631\u0629', es: 'El Huevo - Una Historia Corta', de: 'Das Ei - Eine Kurzgeschichte' },
    description: { en: 'A thought-provoking animated short about the meaning of life.', fr: 'Un court-metrage anime stimulant sur le sens de la vie.', ar: '\u0641\u064A\u0644\u0645 \u0642\u0635\u064A\u0631 \u0645\u062A\u062D\u0631\u0643 \u0645\u062B\u064A\u0631 \u0644\u0644\u062A\u0641\u0643\u064A\u0631 \u0639\u0646 \u0645\u0639\u0646\u0649 \u0627\u0644\u062D\u064A\u0627\u0629.', es: 'Un corto animado que invita a la reflexion sobre el sentido de la vida.', de: 'Ein nachdenklicher Animationsfilm uber den Sinn des Lebens.' },
    url: 'https://www.youtube.com/watch?v=h6fcK_fRYaI',
    embed_url: 'https://www.youtube-nocookie.com/embed/h6fcK_fRYaI',
    thumbnail: 'https://img.youtube.com/vi/h6fcK_fRYaI/hqdefault.jpg',
    source: 'Kurzgesagt',
    type: 'documentary',
    category: 'science',
    subcategory: 'philosophy',
    languages: ['en', 'de'],
    age_range: 'teens',
    duration: 8,
    family_safe: true,
    tags: ['philosophy', 'life', 'animation', 'meaning'],
  },

  // ====================== ADDITIONAL KIDS ======================

  {
    id: 'kids-peekaboo-solar',
    title: { en: 'The Solar System for Kids', fr: 'Le Systeme solaire pour enfants', ar: '\u0627\u0644\u0646\u0638\u0627\u0645 \u0627\u0644\u0634\u0645\u0633\u064A \u0644\u0644\u0623\u0637\u0641\u0627\u0644', es: 'El Sistema Solar para Ninos', de: 'Das Sonnensystem fur Kinder' },
    description: { en: 'Learn about the planets in our solar system.', fr: 'Decouvrez les planetes de notre systeme solaire.', ar: '\u062A\u0639\u0631\u0641 \u0639\u0644\u0649 \u0643\u0648\u0627\u0643\u0628 \u0646\u0638\u0627\u0645\u0646\u0627 \u0627\u0644\u0634\u0645\u0633\u064A.', es: 'Aprende sobre los planetas de nuestro sistema solar.', de: 'Lerne die Planeten unseres Sonnensystems kennen.' },
    url: 'https://www.youtube.com/watch?v=mQrlgH97v94',
    embed_url: 'https://www.youtube-nocookie.com/embed/mQrlgH97v94',
    thumbnail: 'https://img.youtube.com/vi/mQrlgH97v94/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'science',
    subcategory: 'space',
    languages: ['en'],
    age_range: 'kids',
    duration: 20,
    family_safe: true,
    tags: ['solar system', 'planets', 'kids', 'space'],
  },
  {
    id: 'kids-numberblocks',
    title: { en: 'Numberblocks - Learning Numbers', fr: 'Numberblocks - Apprendre les nombres', ar: 'Numberblocks - \u062A\u0639\u0644\u0645 \u0627\u0644\u0623\u0631\u0642\u0627\u0645', es: 'Numberblocks - Aprender Numeros', de: 'Numberblocks - Zahlen lernen' },
    description: { en: 'Fun animated series teaching numbers and arithmetic.', fr: 'Serie animee amusante enseignant les nombres et l\'arithmetique.', ar: '\u0645\u0633\u0644\u0633\u0644 \u0631\u0633\u0648\u0645 \u0645\u062A\u062D\u0631\u0643\u0629 \u0645\u0645\u062A\u0639 \u0644\u062A\u0639\u0644\u064A\u0645 \u0627\u0644\u0623\u0631\u0642\u0627\u0645.', es: 'Serie animada divertida que ensena numeros y aritmetica.', de: 'Lustige Animationsserie zum Erlernen von Zahlen und Rechnen.' },
    url: 'https://www.youtube.com/watch?v=Pv8JEb9OeMo',
    embed_url: 'https://www.youtube-nocookie.com/embed/Pv8JEb9OeMo',
    thumbnail: 'https://img.youtube.com/vi/Pv8JEb9OeMo/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'math',
    languages: ['en'],
    age_range: 'kids',
    duration: 30,
    family_safe: true,
    tags: ['numbers', 'math', 'kids', 'animation'],
  },
  {
    id: 'kids-trotro-fr',
    title: { en: 'Trotro the Donkey', fr: 'L\'ane Trotro', ar: '\u0627\u0644\u062D\u0645\u0627\u0631 \u062A\u0631\u0648\u062A\u0631\u0648', es: 'El Burrito Trotro', de: 'Esel Trotro' },
    description: { en: 'Beloved French cartoon for toddlers.', fr: 'Dessin anime francais adore des tout-petits.', ar: '\u0631\u0633\u0648\u0645 \u0645\u062A\u062D\u0631\u0643\u0629 \u0641\u0631\u0646\u0633\u064A\u0629 \u0645\u062D\u0628\u0648\u0628\u0629 \u0644\u0644\u0635\u063A\u0627\u0631.', es: 'Dibujo animado frances querido para los mas pequenos.', de: 'Beliebter franzosischer Zeichentrickfilm fur Kleinkinder.' },
    url: 'https://www.youtube.com/watch?v=NjUjrPPIv6I',
    embed_url: 'https://www.youtube-nocookie.com/embed/NjUjrPPIv6I',
    thumbnail: 'https://img.youtube.com/vi/NjUjrPPIv6I/hqdefault.jpg',
    source: 'YouTube',
    type: 'video',
    category: 'stories',
    languages: ['fr'],
    age_range: 'kids',
    duration: 20,
    family_safe: true,
    tags: ['french', 'toddlers', 'animation', 'stories'],
  },

  // ====================== ADDITIONAL EDUCATIONAL ======================

  {
    id: 'edu-ted-ed-gravity',
    title: { en: 'What is Gravity?', fr: 'Qu\'est-ce que la gravite?', ar: '\u0645\u0627 \u0647\u064A \u0627\u0644\u062C\u0627\u0630\u0628\u064A\u0629\u061F', es: 'Que es la Gravedad?', de: 'Was ist Schwerkraft?' },
    description: { en: 'TED-Ed explains the fundamental force that shapes the universe.', fr: 'TED-Ed explique la force fondamentale qui faonne l\'univers.', ar: 'TED-Ed \u064A\u0634\u0631\u062D \u0627\u0644\u0642\u0648\u0629 \u0627\u0644\u0623\u0633\u0627\u0633\u064A\u0629 \u0627\u0644\u062A\u064A \u062A\u0634\u0643\u0644 \u0627\u0644\u0643\u0648\u0646.', es: 'TED-Ed explica la fuerza fundamental que da forma al universo.', de: 'TED-Ed erklart die grundlegende Kraft, die das Universum formt.' },
    url: 'https://www.youtube.com/watch?v=oIZA1gEkSCg',
    embed_url: 'https://www.youtube-nocookie.com/embed/oIZA1gEkSCg',
    thumbnail: 'https://img.youtube.com/vi/oIZA1gEkSCg/hqdefault.jpg',
    source: 'TED-Ed',
    type: 'video',
    category: 'science',
    subcategory: 'physics',
    languages: ['en'],
    age_range: 'all',
    duration: 5,
    family_safe: true,
    tags: ['gravity', 'physics', 'space', 'animation'],
  },
  {
    id: 'edu-khan-fractions',
    title: { en: 'Introduction to Fractions', fr: 'Introduction aux fractions', ar: '\u0645\u0642\u062F\u0645\u0629 \u0641\u064A \u0627\u0644\u0643\u0633\u0648\u0631', es: 'Introduccion a las Fracciones', de: 'Einfuhrung in Bruche' },
    description: { en: 'Learn the basics of fractions with Khan Academy.', fr: 'Apprenez les bases des fractions.', ar: '\u062A\u0639\u0644\u0645 \u0623\u0633\u0627\u0633\u064A\u0627\u062A \u0627\u0644\u0643\u0633\u0648\u0631.', es: 'Aprende las bases de las fracciones.', de: 'Lernen Sie die Grundlagen der Bruche.' },
    url: 'https://www.youtube.com/watch?v=atCQKPwFnUY',
    embed_url: 'https://www.youtube-nocookie.com/embed/atCQKPwFnUY',
    thumbnail: 'https://img.youtube.com/vi/atCQKPwFnUY/hqdefault.jpg',
    source: 'Khan Academy',
    type: 'course',
    category: 'math',
    languages: ['en', 'fr', 'es'],
    age_range: 'kids',
    duration: 8,
    family_safe: true,
    tags: ['fractions', 'math', 'education', 'basics'],
  },

  // ====================== ADDITIONAL PODCASTS ======================

  {
    id: 'pod-science-vs',
    title: { en: 'Science Vs', fr: 'Science Vs', ar: 'Science Vs', es: 'Science Vs', de: 'Science Vs' },
    description: { en: 'Pitting facts against popular opinions on hot topics.', fr: 'La science contre les opinions populaires sur les sujets du moment.', ar: '\u0645\u0642\u0627\u0631\u0646\u0629 \u0627\u0644\u062D\u0642\u0627\u0626\u0642 \u0628\u0627\u0644\u0622\u0631\u0627\u0621 \u0627\u0644\u0634\u0627\u0626\u0639\u0629.', es: 'Enfrentando hechos contra opiniones populares.', de: 'Fakten gegen populare Meinungen zu heissen Themen.' },
    url: 'https://gimletmedia.com/shows/science-vs',
    source: 'Spotify',
    type: 'podcast',
    category: 'science',
    languages: ['en'],
    age_range: 'all',
    duration: 40,
    family_safe: true,
    tags: ['science', 'facts', 'myths', 'debate'],
  },
  {
    id: 'pod-six-minutes',
    title: { en: 'Six Minutes - Adventure Stories', fr: 'Six Minutes - Histoires d\'aventure', ar: 'Six Minutes - \u0642\u0635\u0635 \u0645\u063A\u0627\u0645\u0631\u0627\u062A', es: 'Six Minutes - Historias de aventura', de: 'Six Minutes - Abenteuergeschichten' },
    description: { en: 'A mystery-adventure podcast for kids.', fr: 'Un podcast mystere-aventure pour enfants.', ar: '\u0628\u0648\u062F\u0643\u0627\u0633\u062A \u063A\u0645\u0648\u0636-\u0645\u063A\u0627\u0645\u0631\u0627\u062A \u0644\u0644\u0623\u0637\u0641\u0627\u0644.', es: 'Un podcast de misterio y aventura para ninos.', de: 'Ein Mysterien-Abenteuer-Podcast fur Kinder.' },
    url: 'https://www.sixminutespodcast.com/',
    source: 'Apple Podcasts',
    type: 'podcast',
    category: 'stories',
    languages: ['en'],
    age_range: 'kids',
    duration: 20,
    family_safe: true,
    tags: ['kids', 'adventure', 'mystery', 'stories'],
  },

  // ====================== ADDITIONAL LIVE ======================

  {
    id: 'live-france24-ar',
    title: { en: 'France 24 Live (Arabic)', fr: 'France 24 en direct (Arabe)', ar: 'France 24 \u0645\u0628\u0627\u0634\u0631 (\u0639\u0631\u0628\u064A)', es: 'France 24 en vivo (Arabe)', de: 'France 24 Live (Arabisch)' },
    description: { en: 'France 24 Arabic live stream.', fr: 'France 24 en arabe en direct.', ar: '\u0641\u0631\u0646\u0633 24 \u0628\u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0645\u0628\u0627\u0634\u0631.', es: 'France 24 en arabe en directo.', de: 'France 24 auf Arabisch live.' },
    url: 'https://www.youtube.com/watch?v=PpGSkwJuGmQ',
    embed_url: 'https://www.youtube-nocookie.com/embed/PpGSkwJuGmQ',
    thumbnail: 'https://img.youtube.com/vi/PpGSkwJuGmQ/hqdefault.jpg',
    source: 'France 24',
    type: 'live_tv',
    category: 'news',
    languages: ['ar'],
    age_range: 'all',
    family_safe: true,
    tags: ['news', 'live', 'arabic', 'french'],
  },
  {
    id: 'live-dw-ar',
    title: { en: 'DW Arabic Live', fr: 'DW Arabe en direct', ar: 'DW \u0639\u0631\u0628\u064A \u0645\u0628\u0627\u0634\u0631', es: 'DW Arabe en vivo', de: 'DW Arabisch Live' },
    description: { en: 'Deutsche Welle Arabic live news.', fr: 'Deutsche Welle en arabe en direct.', ar: '\u0623\u062E\u0628\u0627\u0631 Deutsche Welle \u0628\u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0645\u0628\u0627\u0634\u0631.', es: 'Noticias en arabe de Deutsche Welle en directo.', de: 'Deutsche Welle auf Arabisch live.' },
    url: 'https://www.youtube.com/watch?v=vVtnNiI5b-M',
    embed_url: 'https://www.youtube-nocookie.com/embed/vVtnNiI5b-M',
    thumbnail: 'https://img.youtube.com/vi/vVtnNiI5b-M/hqdefault.jpg',
    source: 'DW',
    type: 'live_tv',
    category: 'news',
    languages: ['ar'],
    age_range: 'all',
    family_safe: true,
    tags: ['news', 'live', 'arabic', 'german'],
  },
  {
    id: 'live-dw-es',
    title: { en: 'DW Spanish Live', fr: 'DW Espagnol en direct', ar: 'DW \u0625\u0633\u0628\u0627\u0646\u064A \u0645\u0628\u0627\u0634\u0631', es: 'DW en Espanol en vivo', de: 'DW Spanisch Live' },
    description: { en: 'Deutsche Welle Spanish live news.', fr: 'Deutsche Welle en espagnol en direct.', ar: 'Deutsche Welle \u0628\u0627\u0644\u0625\u0633\u0628\u0627\u0646\u064A\u0629 \u0645\u0628\u0627\u0634\u0631.', es: 'Noticias en espanol de Deutsche Welle en directo.', de: 'Deutsche Welle auf Spanisch live.' },
    url: 'https://www.youtube.com/watch?v=OEHAV4ISEkA',
    embed_url: 'https://www.youtube-nocookie.com/embed/OEHAV4ISEkA',
    thumbnail: 'https://img.youtube.com/vi/OEHAV4ISEkA/hqdefault.jpg',
    source: 'DW',
    type: 'live_tv',
    category: 'news',
    languages: ['es'],
    age_range: 'all',
    family_safe: true,
    tags: ['news', 'live', 'spanish'],
  },

  // ====================== MORE EDUCATIONAL ======================

  {
    id: 'edu-ted-creativity',
    title: { en: 'Do Schools Kill Creativity?', fr: "L'ecole tue-t-elle la creativite?", ar: '\u0647\u0644 \u062A\u0642\u062A\u0644 \u0627\u0644\u0645\u062F\u0627\u0631\u0633 \u0627\u0644\u0625\u0628\u062F\u0627\u0639\u061F', es: 'Las escuelas matan la creatividad?', de: 'Toten Schulen die Kreativitat?' },
    description: { en: 'Sir Ken Robinson\'s iconic TED talk about education and creativity.', fr: 'La conference TED iconique de Sir Ken Robinson.', ar: '\u0645\u062D\u0627\u062F\u062B\u0629 TED \u0627\u0644\u0634\u0647\u064A\u0631\u0629 \u0644\u0633\u064A\u0631 \u0643\u064A\u0646 \u0631\u0648\u0628\u0646\u0633\u0648\u0646.', es: 'La iconica charla TED de Sir Ken Robinson.', de: 'Sir Ken Robinsons ikonischer TED-Vortrag.' },
    url: 'https://www.youtube.com/watch?v=iG9CE55wbtY',
    embed_url: 'https://www.youtube-nocookie.com/embed/iG9CE55wbtY',
    thumbnail: 'https://img.youtube.com/vi/iG9CE55wbtY/hqdefault.jpg',
    source: 'TED',
    type: 'video',
    category: 'science',
    subcategory: 'education',
    languages: ['en'],
    age_range: 'all',
    duration: 20,
    family_safe: true,
    tags: ['education', 'creativity', 'ted talk'],
  },
  {
    id: 'edu-khan-geometry',
    title: { en: 'Introduction to Geometry', fr: 'Introduction a la geometrie', ar: '\u0645\u0642\u062F\u0645\u0629 \u0641\u064A \u0627\u0644\u0647\u0646\u062F\u0633\u0629', es: 'Introduccion a la Geometria', de: 'Einfuhrung in die Geometrie' },
    description: { en: 'Learn the fundamentals of geometry with Khan Academy.', fr: 'Apprenez les bases de la geometrie.', ar: '\u062A\u0639\u0644\u0645 \u0623\u0633\u0627\u0633\u064A\u0627\u062A \u0627\u0644\u0647\u0646\u062F\u0633\u0629 \u0645\u0639 \u0623\u0643\u0627\u062F\u064A\u0645\u064A\u0629 \u062E\u0627\u0646.', es: 'Aprende los fundamentos de la geometria.', de: 'Lernen Sie die Grundlagen der Geometrie.' },
    url: 'https://www.youtube.com/watch?v=ilFnSduzMOw',
    embed_url: 'https://www.youtube-nocookie.com/embed/ilFnSduzMOw',
    thumbnail: 'https://img.youtube.com/vi/ilFnSduzMOw/hqdefault.jpg',
    source: 'Khan Academy',
    type: 'course',
    category: 'math',
    languages: ['en', 'fr', 'es'],
    age_range: 'teens',
    duration: 10,
    family_safe: true,
    tags: ['geometry', 'math', 'education'],
  },
  {
    id: 'edu-crashcourse-economics',
    title: { en: 'CrashCourse Economics', fr: 'CrashCourse Economie', ar: 'CrashCourse \u0627\u0642\u062A\u0635\u0627\u062F', es: 'CrashCourse Economia', de: 'CrashCourse Wirtschaft' },
    description: { en: 'An entertaining introduction to economics.', fr: 'Une introduction divertissante a l\'economie.', ar: '\u0645\u0642\u062F\u0645\u0629 \u0645\u0645\u062A\u0639\u0629 \u0641\u064A \u0627\u0644\u0627\u0642\u062A\u0635\u0627\u062F.', es: 'Una introduccion entretenida a la economia.', de: 'Eine unterhaltsame Einfuhrung in die Wirtschaft.' },
    url: 'https://www.youtube.com/watch?v=3ez10ADR_gM',
    embed_url: 'https://www.youtube-nocookie.com/embed/3ez10ADR_gM',
    thumbnail: 'https://img.youtube.com/vi/3ez10ADR_gM/hqdefault.jpg',
    source: 'CrashCourse',
    type: 'course',
    category: 'science',
    subcategory: 'economics',
    languages: ['en'],
    age_range: 'teens',
    duration: 12,
    family_safe: true,
    tags: ['economics', 'finance', 'education'],
  },
];

// ---------- Catalog Query Helpers ----------

export function getContentByTab(tab: WatchTab, locale: string): ContentItem[] {
  switch (tab) {
    case 'for_you':
      return CONTENT_CATALOG.filter((c) => c.family_safe && (c.languages.includes(locale) || c.featured));
    case 'documentaries':
      return CONTENT_CATALOG.filter((c) => c.type === 'documentary');
    case 'learn':
      return CONTENT_CATALOG.filter((c) => c.type === 'course' || c.type === 'video' || c.type === 'channel')
        .filter((c) => c.category !== 'islamic' && c.category !== 'quran' && c.age_range !== 'kids');
    case 'kids':
      return CONTENT_CATALOG.filter((c) => c.age_range === 'kids');
    case 'podcasts':
      return CONTENT_CATALOG.filter((c) => c.type === 'podcast');
    case 'live':
      return CONTENT_CATALOG.filter((c) => c.type === 'live_tv' || c.type === 'live_radio');
    case 'islamic':
      return CONTENT_CATALOG.filter((c) => c.category === 'islamic' || c.category === 'quran');
    default:
      return CONTENT_CATALOG;
  }
}

export function getFeaturedContent(): ContentItem[] {
  return CONTENT_CATALOG.filter((c) => c.featured);
}

export function searchContent(query: string, items: ContentItem[], locale: string): ContentItem[] {
  const q = query.toLowerCase().trim();
  if (!q) return items;
  return items.filter((item) => {
    const title = (item.title[locale] || item.title.en || '').toLowerCase();
    const desc = (item.description[locale] || item.description.en || '').toLowerCase();
    const tags = item.tags.join(' ').toLowerCase();
    return title.includes(q) || desc.includes(q) || tags.includes(q) || item.source.toLowerCase().includes(q) || item.category.includes(q);
  });
}

export function filterContent(
  items: ContentItem[],
  filters: { language?: string; category?: string; ageRange?: string; duration?: 'short' | 'medium' | 'long' },
): ContentItem[] {
  return items.filter((item) => {
    if (filters.language && !item.languages.includes(filters.language)) return false;
    if (filters.category && item.category !== filters.category) return false;
    if (filters.ageRange && item.age_range !== filters.ageRange) return false;
    if (filters.duration) {
      const d = item.duration ?? 0;
      if (filters.duration === 'short' && d > 10) return false;
      if (filters.duration === 'medium' && (d <= 10 || d > 30)) return false;
      if (filters.duration === 'long' && d <= 30) return false;
    }
    return true;
  });
}
